import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { checkSiren } from '../utils/checkSiren';
import { createPortal } from 'react-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { Plus, Trash2, Search, Loader2, AlignLeft, Layers, FileSignature, CheckCircle2, Ban, RotateCcw, ChevronDown, Tablet, Monitor, Mail, Link as LinkIcon, Copy, Send, X as XIcon, CreditCard, Printer, Truck, Eye, FileText, Upload, User, Download } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import { SidePanel } from '../components/SidePanel';
import { ClientSearchField } from '../components/ClientSearchField';
import DocumentsTab from '../components/DocumentsTab';
import ReglementPanel from '../components/ReglementPanel';
import { ActivitesFichePanel, useHasActivite, ActivityPulse } from '../components/ActivitesFichePanel';
import EmailComposeModal from '../components/EmailComposeModal';
import { useSociete } from '../context/SocieteContext';
import { useAuth } from '../context/AuthContext';
import { todayInTz, wallTimeInTzToIso, fmtDateTime } from '../utils/dateUtils';

const STATUTS = {
  brouillon:            { label: 'Brouillon',            bg: '#F3F4F6', color: '#374151' },
  en_attente_signature: { label: 'En attente signature', bg: '#FEF3C7', color: '#92400E' },
  signe:                { label: 'Signé / Réservé',      bg: '#DBEAFE', color: '#1E40AF' },
  en_cours:             { label: 'Actif / En cours',     bg: '#DCFCE7', color: '#166534' },
  termine:              { label: 'Clôturé',              bg: '#E5E7EB', color: '#374151' },
  annule:               { label: 'Annulé',               bg: '#FEE2E2', color: '#991B1B' },
};

const STATUTS_LOGISTIQUE = {
  non_livre:              { label: 'Non livré',            bg: '#F3F4F6', color: '#374151' },
  a_livrer:               { label: 'A livrer',             bg: '#DBEAFE', color: '#1E40AF' },
  sur_site:               { label: 'Sur site',             bg: '#DCFCE7', color: '#166534' },
  partiellement_restitue: { label: 'Part. restitué',       bg: '#FEF3C7', color: '#92400E' },
  restitue:               { label: 'Totalement restitué',  bg: '#EDE9FE', color: '#5B21B6' },
};

const STATUTS_FACTURATION = {
  non_facturable:     { label: 'Non facturable',    bg: '#F3F4F6', color: '#374151' },
  encours_a_facturer: { label: 'Encours à facturer (TTC)', bg: '#FEF3C7', color: '#92400E' },
  brouillon:          { label: 'Brouillon',          bg: '#DBEAFE', color: '#1E40AF' },
  facture_a_jour:     { label: 'Facturé à jour',     bg: '#DCFCE7', color: '#166534' },
  restitue_a_solder:  { label: 'Restitué à solder !', bg: '#EDE9FE', color: '#5B21B6' },
};

const NIVEAUX_CARBURANT = ['PLEIN', '3/4', '1/2', '1/4', 'VIDE'];


const JOURS_SEMAINE = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

// Calcule le nombre de jours facturables entre deux dates en excluant les indices de jours donnés
// (0=Lundi … 6=Dimanche, convention Python weekday == notre convention JS convertie)
function calcDureeWorkingDays(dateDebut, dateFin, joursExclus = [5, 6]) {
  if (!dateDebut || !dateFin) return 0;
  const exclu = new Set(joursExclus.map(Number));
  let count = 0;
  const fin = new Date(dateFin + 'T12:00:00');
  let cur = new Date(dateDebut + 'T12:00:00');
  while (cur <= fin) {
    const jsDay = cur.getDay(); // 0=Dim, 1=Lun, …, 6=Sam
    const ourDay = jsDay === 0 ? 6 : jsDay - 1; // 0=Lun … 6=Dim
    if (!exclu.has(ourDay)) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return Math.max(1, count);
}

// Cherche le prix/jour dans la grille tarifaire dégressif pour une durée donnée
function getPrixJourV2(duree, grilleTarifaire) {
  if (!grilleTarifaire || !grilleTarifaire.length) return null;
  const sorted = [...grilleTarifaire].sort((a, b) => (a.duree_min || 0) - (b.duree_min || 0));
  for (const t of sorted) {
    const dmin = t.duree_min || 1;
    const dmax = t.duree_max;
    if (duree >= dmin && (dmax == null || duree <= dmax)) return t.prix_jour ?? null;
  }
  return null;
}

const TYPES_AVENANT = {
  PROLONGATION:          'Prolongation',
  AJUSTEMENT_TARIF:      'Ajustement tarifaire',
  AJOUT_MATERIEL:        'Ajout de matériel',
  RETRAIT_MATERIEL:      'Retrait de matériel',
  AJOUT_SERVICE:         'Ajout de service',
  RETRAIT_SERVICE:       'Retrait de service',
  MODIFICATION_SERVICES: 'Modification des services',
};

const TYPES_AVENANT_CREATION = {
  PROLONGATION:      'Prolongation',
  AJUSTEMENT_TARIF:  'Ajustement tarifaire',
  AJOUT_MATERIEL:    'Ajout de matériel',
  RETRAIT_MATERIEL:  'Retrait de matériel',
};

const SVC_TYPE_OPTS = [
  { value: '',           label: 'Tous types' },
  { value: 'prestation', label: 'Prestation' },
  { value: 'texte',      label: 'Commentaire' },
  { value: 'section',    label: 'Section' },
];

const SVC_TYPE_COLOR = {
  prestation: { bg: '#F0FDF4', color: '#16A34A' },
  texte:      { bg: '#FFFBEB', color: '#B45309' },
  section:    { bg: '#EEF2FF', color: '#4F46E5' },
};



const STATUTS_AVENANT = {
  brouillon: { label: 'Brouillon', bg: '#F3F4F6', color: '#374151' },
  valide:    { label: 'Validé',    bg: '#DCFCE7', color: '#166534' },
  annule:    { label: 'Annulé',    bg: '#FEE2E2', color: '#991B1B' },
};

const emptyAvenantForm = () => ({
  type_avenant: 'PROLONGATION', ligne_id: '', date_effet: '', motif: '',
  nouvelle_date_fin: '', nouveau_prix_jour: '', nouvelle_quantite: '',
  ajoutMode: 'existante', nouvelle_categorie_id: '', nouvelle_gamme_id: '',
  nouvelle_ligne_quantite: 1, nouvelle_ligne_date_debut: '', nouvelle_ligne_date_fin: '', nouvelle_ligne_prix: '',
});

const emptySuspensionForm = () => ({
  ligne_contrat_id: '', motif_id: '', date_debut: '', date_fin: '', taux_facturation: 0, description: '',
});



const emptyLigne = () => ({
  categorie_id: '', categorie_nom: '', gamme_id: '', gamme_nom: '',
  quantite: 1, date_debut_prevue: '', date_fin_prevue: '', prix_unitaire_jour: '',
  prix_est_forfait: false,
  jours_exclus: [5, 6], note: '',
});

const emptyForm = () => ({
  client: { client_id: null, client_type: null, client_nom: null, tiers_id: null },
  contact_id: null, contact_nom: null,
  commercial_id: null, commercial_nom: null,
  titre: '', ref_client: '', adresse_livraison: '', mode_reglement: '', conditions_paiement_nom: '', notes: '',
});

function fmtEur(v) {
  return Number(v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

function fmtDate(v) {
  if (!v) return '—';
  try {
    const s = String(v);
    // Date seule (YYYY-MM-DD) : midi UTC → insensible au fuseau du navigateur (DOM-TOM UTC-x)
    if (!s.includes('T') && /^\d{4}-\d{2}-\d{2}$/.test(s.slice(0, 10))) {
      const [y, m, d] = s.slice(0, 10).split('-').map(Number);
      return new Date(Date.UTC(y, m - 1, d, 12, 0, 0)).toLocaleDateString('fr-FR');
    }
    return new Date(v).toLocaleDateString('fr-FR');
  } catch { return v; }
}

function StatutBadge({ statut }) {
  // reserve / confirme sont des alias visuels de "signe" ("Signé / Réservé")
  const normalized = (statut === 'reserve' || statut === 'confirme') ? 'signe' : statut;
  const s = STATUTS[normalized] || STATUTS.brouillon;
  return (
    <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: s.bg, color: s.color }} data-testid={`statut-badge-${statut}`}>
      {s.label}
    </span>
  );
}

function LogistiqueBadge({ statut }) {
  const s = STATUTS_LOGISTIQUE[statut] || STATUTS_LOGISTIQUE.non_livre;
  return (
    <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: s.bg, color: s.color }} data-testid={`logistique-badge-${statut}`}>
      {s.label}
    </span>
  );
}

function FacturationBadge({ statut }) {
  const s = STATUTS_FACTURATION[statut] || STATUTS_FACTURATION.non_facturable;
  return (
    <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: s.bg, color: s.color }} data-testid={`facturation-badge-${statut}`}>
      {s.label}
    </span>
  );
}

function FilterDropdown({ icon, label, value, big, options, selected, onToggle, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  const active = selected.length > 0;
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen(o => !o)} data-testid={testid}
        className={big
          ? 'w-full text-left p-3 rounded-xl border transition-colors'
          : 'flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-semibold transition-colors'}
        style={{
          background: active ? '#EEF2FF' : 'var(--card)',
          borderColor: active ? '#4338CA' : 'var(--border)',
          borderWidth: active ? 2 : 1,
          color: 'var(--text)',
        }}>
        {big ? (
          <>
            <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide" style={{ color: active ? '#4338CA' : 'var(--text-muted)' }}>
              <span>{icon} {label}</span>
              <ChevronDown size={12} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
            </div>
            <div className="text-xl font-bold mt-0.5" style={{ color: active ? '#4338CA' : 'var(--text)' }}>{value}</div>
          </>
        ) : (
          <>
            <span style={{ color: active ? '#4338CA' : 'var(--text-muted)' }}>{icon} {label}</span>
            {active && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold" style={{ background: '#4338CA', color: '#fff' }}>{selected.length}</span>}
            <ChevronDown size={11} style={{ color: 'var(--text-muted)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
          </>
        )}
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-56 rounded-lg border shadow-lg p-1.5 space-y-0.5" style={{ background: 'var(--card)', borderColor: 'var(--border)' }} data-testid={`${testid}-menu`}>
          {options.map(o => (
            <label key={o.key} className="flex items-center gap-2 px-2 py-1.5 rounded-md text-xs cursor-pointer hover:bg-gray-50" data-testid={`${testid}-opt-${o.key}`}>
              <input type="checkbox" checked={selected.includes(o.key)} onChange={() => onToggle(o.key)} />
              <span className="flex-1" style={{ color: 'var(--text)' }}>{o.label}</span>
              <span className="font-semibold" style={{ color: 'var(--text-muted)' }}>{o.count}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── LigneSvcRowV2 — ligne de tableau services (parité exacte V1) ─────────────
const TVA_OPTS_V2 = [
  { value: 0,    label: '0 %'   },
  { value: 5.5,  label: '5,5 %' },
  { value: 8.5,  label: '8,5 %' },
  { value: 20,   label: '20 %'  },
];

function LigneSvcRowV2({ s, isFinancialLocked, onPatch, onDelete, onRetraitService, recapLocationHT }) {
  const isPourcentage = s.mode_facturation === 'POURCENTAGE';
  const isParJour     = s.mode_facturation === 'PAR_JOUR';
  // Même formule que le récapitulatif : montant_estime serveur, sinon % du total location,
  // sinon qté × PU. Pour PAR_JOUR : affichage estimatif qté × PU (le réel est calculé à la facturation).
  const montantSvc = s.montant_estime != null
    ? Number(s.montant_estime)
    : isPourcentage
      ? (Number(s.pourcentage_application) || 0) / 100 * (Number(recapLocationHT) || 0)
      : (Number(s.quantite) || 0) * (Number(s.prix_unitaire_ht) || 0);
  const totalHt = montantSvc.toFixed(2);
  const isAuRetour = s.mode_facturation === 'RESTITUTION';
  const isAnnule   = s.statut_service === 'annule';
  const isSection  = s.type_ligne === 'section';
  const isTexte    = s.type_ligne === 'texte';

  // ── Cycle de mode : FORFAIT_UNIQUE → PAR_JOUR → RESTITUTION → FORFAIT_UNIQUE
  const MODE_CYCLE = ['FORFAIT_UNIQUE', 'PAR_JOUR', 'RESTITUTION'];
  const cycleMode = () => {
    const cur = s.mode_facturation || 'FORFAIT_UNIQUE';
    const idx = MODE_CYCLE.indexOf(cur);
    const newMode = MODE_CYCLE[(idx + 1) % MODE_CYCLE.length];
    onPatch('mode_facturation', newMode);
    // En PAR_JOUR, la quantité est un multiplicateur journalier (nb d'unités/jour),
    // PAS le nombre de jours. Si l'ancienne valeur était > 1 (ex: nb_jours saisi
    // manuellement pour un FORFAIT), la remettre à 1 pour éviter 20j × (20×250) = 100 000€.
    if (newMode === 'PAR_JOUR' && parseFloat(s.quantite ?? 1) !== 1) {
      onPatch('quantite', 1);
    }
  };

  const modeLabel = isAuRetour ? '↩ Retour' : isParJour ? '⏱ /jour' : '1× Forfait';
  const modeBg    = isAuRetour ? '#DBEAFE' : isParJour  ? '#D1FAE5' : '#F3F4F6';
  const modeColor = isAuRetour ? '#1E40AF' : isParJour  ? '#065F46' : '#6B7280';

  if (isSection) return (
    <tr style={{ background: '#EEF2FF' }}>
      <td colSpan={9} className="px-3 py-1.5 font-bold text-xs" style={{ color: '#4F46E5' }}>{s.designation}</td>
    </tr>
  );
  if (isTexte) return (
    <tr style={{ background: '#FFFBEB' }}>
      <td colSpan={9} className="px-3 py-1.5 italic text-xs" style={{ color: '#B45309' }}>{s.designation}</td>
    </tr>
  );

  const rowStyle = isAnnule
    ? { opacity: 0.5, textDecoration: 'line-through', background: '#FEF2F2' }
    : {};

  return (
    <tr style={{ borderBottom: '1px solid var(--border)', ...rowStyle }}>
      {/* Mode facturation — cycle 3 états */}
      <td className="px-2 py-1.5">
        <button type="button"
          disabled={isFinancialLocked || isAnnule || isPourcentage}
          onClick={cycleMode}
          title={isParJour ? 'Mode par jour — facturation automatique × nb jours loués' : isAuRetour ? 'Facturé au retour du matériel' : 'Forfait unique'}
          className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded font-medium whitespace-nowrap"
          style={{ background: modeBg, color: modeColor, opacity: isPourcentage ? 0.4 : 1 }}>
          {modeLabel}
        </button>
      </td>
      {/* Réf */}
      <td className="px-2 py-1.5 text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>
        {s.article_reference || '—'}
      </td>
      {/* Désignation */}
      <td className="px-2 py-1.5">
        <input
          value={s.designation || ''}
          disabled={isFinancialLocked || isAnnule}
          onChange={e => onPatch('designation', e.target.value)}
          className="w-full bg-transparent text-xs focus:outline-none focus:ring-1 focus:ring-blue-300 rounded px-1"
          style={{ minWidth: 120 }}
        />
      </td>
      {/* Qté */}
      <td className="px-2 py-1.5 text-right">
        <input type="number"
          value={s.quantite ?? 1}
          disabled={isFinancialLocked || isAnnule}
          onChange={e => onPatch('quantite', e.target.value)}
          className="w-14 bg-transparent text-xs text-right focus:outline-none focus:ring-1 focus:ring-blue-300 rounded px-1"
        />
      </td>
      {/* PU Net */}
      <td className="px-2 py-1.5 text-right">
        {isPourcentage ? (
          <span className="text-xs px-1" style={{ color: 'var(--text-muted)' }} title="Pourcentage du montant location HT">
            {Number(s.pourcentage_application) || 0} %
          </span>
        ) : (
          <input type="number"
            value={s.prix_unitaire_ht ?? 0}
            disabled={isFinancialLocked || isAnnule}
            onChange={e => onPatch('prix_unitaire_ht', e.target.value)}
            className="w-20 bg-transparent text-xs text-right focus:outline-none focus:ring-1 focus:ring-blue-300 rounded px-1"
          />
        )}
      </td>
      {/* TVA */}
      <td className="px-2 py-1.5 text-right">
        <select
          value={s.taux_tva ?? 20}
          disabled={isFinancialLocked || isAnnule}
          onChange={e => onPatch('taux_tva', parseFloat(e.target.value))}
          className="bg-transparent text-xs border-0 focus:outline-none"
        >
          {TVA_OPTS_V2.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </td>
      {/* Total HT */}
      <td className="px-2 py-1.5 text-right text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
        {totalHt} €
      </td>
      {/* Delete */}
      <td className="px-1 py-1.5 text-center">
        <button type="button" onClick={onDelete}
          className="p-1 rounded hover:bg-red-50 transition-colors"
          style={{ color: '#EF4444' }}>
          <Trash2 size={13} />
        </button>
      </td>
    </tr>
  );
}

// ─── SvcAddRowV2 — copie STRICTE du SvcAddRow V1, adapté en div pour le panneau V2 ─────
function SvcAddRowV2({ onSelect }) {
  const [query, setQuery]           = useState('');
  const [results, setResults]       = useState([]);
  const [open, setOpen]             = useState(false);
  const [loading, setLoading]       = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const inputRef     = useRef();
  const containerRef = useRef();
  const [dropPos, setDropPos]       = useState({ top: 0, left: 0, width: 0 });

  const isSpecial = typeFilter === 'texte' || typeFilter === 'section';

  const search = useCallback(async (q) => {
    if (isSpecial) return;
    setLoading(true);
    try {
      const params = { limit: 40 };
      if (typeFilter) params.type = typeFilter;
      if (q?.trim()) params.q = q.trim();
      const r = await api.getArticles(params);
      setResults(r.data?.items || []);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, [isSpecial, typeFilter]);

  useEffect(() => {
    const t = setTimeout(() => { if (!isSpecial) search(query); }, 250);
    return () => clearTimeout(t);
  }, [query, search, isSpecial]);

  useEffect(() => {
    const h = e => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const openDrop = () => {
    if (isSpecial || open) return;
    const rect = inputRef.current?.getBoundingClientRect();
    if (rect) setDropPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX, width: rect.width });
    setOpen(true);
    if (results.length === 0) search(query);
  };

  const selectSpecial = () => {
    if (!query.trim()) return;
    onSelect({ article_id: null, designation: query, type_ligne: typeFilter, quantite: 1, prix_unitaire_ht: 0 });
    setQuery(''); setOpen(false);
  };

  const selectArticle = art => {
    onSelect({ article_id: art.article_id, designation: art.designation, type_ligne: 'prestation', quantite: 1, prix_unitaire_ht: Number(art.prix_vente) || 0 });
    setQuery(''); setResults([]); setOpen(false);
  };

  const handleKeyDown = e => {
    if (isSpecial && e.key === 'Enter' && query.trim()) { e.preventDefault(); selectSpecial(); return; }
    if (!isSpecial) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); openDrop(); }
      if (e.key === 'Enter' && results.length > 0) selectArticle(results[0]);
    }
  };

  const placeholder = typeFilter === 'section' ? 'Titre de la section…'
    : typeFilter === 'texte' ? 'Texte libre du commentaire…'
    : 'Rechercher une prestation…';

  const tl = typeFilter || 'prestation';
  const col = SVC_TYPE_COLOR[tl] || SVC_TYPE_COLOR.prestation;

  return (
    <div ref={containerRef} className="flex items-stretch gap-2 w-full" data-testid="svc-v2-add-row">
      <select value={typeFilter}
        onChange={e => { setTypeFilter(e.target.value); setQuery(''); setResults([]); setOpen(false); }}
        data-testid="svc-v2-type-select"
        className="flex-shrink-0 text-xs border rounded-lg px-2 py-1.5 font-medium"
        style={{ borderColor: 'var(--border)', background: 'var(--bg)', color: 'var(--text-primary)' }}>
        {SVC_TYPE_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>

      <div className="flex-1 relative">
        <div className="flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 focus-within:ring-1 focus-within:ring-blue-200"
          style={{ borderColor: isSpecial ? col.color : 'var(--border)', background: '#fff' }}>
          {isSpecial
            ? (typeFilter === 'section'
                ? <Layers size={12} style={{ color: col.color, flexShrink: 0 }} />
                : <AlignLeft size={12} style={{ color: col.color, flexShrink: 0 }} />)
            : <Search size={12} style={{ color: '#9CA3AF', flexShrink: 0 }} />}
          <input ref={inputRef} value={query}
            onChange={e => { setQuery(e.target.value); if (!isSpecial) openDrop(); }}
            onFocus={openDrop}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            data-testid="svc-v2-article-search"
            className="flex-1 text-xs bg-transparent focus:outline-none"
            style={{ color: isSpecial ? col.color : '#374151',
                     fontStyle: typeFilter === 'texte' ? 'italic' : 'normal',
                     fontWeight: typeFilter === 'section' ? 700 : 400 }} />
          {loading && <Loader2 size={12} className="animate-spin" style={{ color: '#9CA3AF', flexShrink: 0 }} />}
          {isSpecial && query.trim() && (
            <button type="button" onMouseDown={e => { e.preventDefault(); selectSpecial(); }}
              data-testid="svc-v2-special-add-btn"
              className="text-xs px-2 py-0.5 rounded font-semibold flex-shrink-0"
              style={{ background: col.bg, color: col.color }}>Ajouter</button>
          )}
        </div>
        {!isSpecial && open && (
          <div style={{ position: 'fixed', top: dropPos.top + 4, left: dropPos.left,
                        width: Math.max(dropPos.width, 300), zIndex: 9999,
                        background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8,
                        boxShadow: '0 8px 24px rgba(0,0,0,.12)', maxHeight: 280, overflowY: 'auto' }}>
            {loading && <div className="px-3 py-2 text-xs text-slate-400">Chargement…</div>}
            {!loading && results.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">Aucun article trouvé</div>}
            {results.map(art => (
              <button key={art.article_id} type="button" onMouseDown={() => selectArticle(art)}
                data-testid={`svc-v2-article-${art.article_id}`}
                className="w-full text-left flex items-center gap-3 px-3 py-2 hover:bg-slate-50"
                style={{ borderBottom: '1px solid #F1F5F9' }}>
                <span className="text-[10px] font-mono text-slate-400 flex-shrink-0">{art.reference || '—'}</span>
                <span className="text-xs text-slate-700 truncate flex-1">{art.designation}</span>
                <span className="text-xs text-slate-400 flex-shrink-0">{fmtEur(art.prix_vente)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


export default function LocationsV2() {
  const { activeAgences, isReadOnly, activeSocieteTimezone } = useSociete();
  const [contrats, setContrats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  // Debounce 300ms : évite une requête par frappe et les race conditions
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [categories, setCategories] = useState([]);
  const [gammesByCat, setGammesByCat] = useState({});
  const [form, setForm] = useState(emptyForm());
  const [lignes, setLignes] = useState([emptyLigne()]);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);  // garde synchrone anti double-clic (l'état React est asynchrone)
  const { hasActivity } = useHasActivite('location_v2', editing?.contrat_id);
  const [activeTab, setActiveTab] = useState('info');
  const [bls, setBls] = useState([]);
  const [loadingBls, setLoadingBls] = useState(false);
  const [voirControlesBlId, setVoirControlesBlId] = useState(null);
  const [voirBlDetail, setVoirBlDetail] = useState(null);
  const [controlesBl, setControlesBl] = useState([]);
  const [loadingControles, setLoadingControles] = useState(false);
  const [materielsDispo, setMaterielsDispo] = useState({});
  const [blSel, setBlSel] = useState({});         // ligne_id → materielId sélectionné
  const [blSelDate, setBlSelDate] = useState({});  // ligne_id → date_prevue (datetime-local)
  const [blRetourDate, setBlRetourDate] = useState({}); // equipement_id → date retour prévue (datetime-local)
  const [savingBl, setSavingBl] = useState(false);
  const [reservingLine, setReservingLine] = useState(null); // ligne_id en cours de réservation
  const [avenants, setAvenants] = useState([]);
  const [loadingAvenants, setLoadingAvenants] = useState(false);
  const [avenantForm, setAvenantForm] = useState(null);
  const [savingAvenant, setSavingAvenant] = useState(false);
  const [suspensions, setSuspensions] = useState([]);
  const [loadingSuspensions, setLoadingSuspensions] = useState(false);
  const [suspensionForm, setSuspensionForm] = useState(null);
  const [savingSuspension, setSavingSuspension] = useState(false);
  const [motifsSuspension, setMotifsSuspension] = useState([]);
  const [retraitServiceForm, setRetraitServiceForm] = useState(null);
  const [savingRetraitService, setSavingRetraitService] = useState(false);
  const [localServices, setLocalServices]       = useState([]);
  const [pendingServiceIds, setPendingServiceIds] = useState(new Set());
  const [initialServices, setInitialServices] = useState([]); // baseline services formalisés (diff avenant)
  const [initialLignes, setInitialLignes]         = useState([]);
  const [savingSession, setSavingSession]         = useState(false);
  const [expandedAvenants, setExpandedAvenants]   = useState(new Set());
  const [factures, setFactures] = useState([]);
  const [docCount, setDocCount]     = useState(0);
  const [loadingFactures, setLoadingFactures] = useState(false);
  const [facturationPeriodeFin, setFacturationPeriodeFin] = useState('');
  const [facturationRefClient, setFacturationRefClient] = useState('');
  const [previewFacture, setPreviewFacture] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmingFacture, setConfirmingFacture] = useState(false);
  const [filtreStatut, setFiltreStatut] = useState([]);
  const [filtreLogistique, setFiltreLogistique] = useState([]);
  const [filtreFacturation, setFiltreFacturation] = useState([]);
  const [filtreLivraisons, setFiltreLivraisons] = useState([]);
  const [filtreAlertes, setFiltreAlertes] = useState([]);
  const toggleFiltre = (setter) => (key) => setter(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  const [kpis, setKpis] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [orgUsers, setOrgUsers] = useState([]);
  useEffect(() => {
    api.getUsers()
      .then(r => setOrgUsers((r.data || []).filter(u => u.actif !== false)))
      .catch(() => {});
  }, []);
  const [contactDropOpen, setContactDropOpen] = useState(false);
  const contactDropRef = useRef(null);
  const notesRef = useRef(null);
  const initialClientIdV2 = useRef(null);
  const lastConditionsKeyRef = useRef(null); // tracking pour rechargement conditions de règlement

  // Auto-resize du champ Notes — via ref callback (fonctionne quel que soit l'onglet actif)
  const notesRefCallback = useCallback(el => {
    notesRef.current = el;
    if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; }
  }, [form.notes]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── États "Envoyer le devis" ──────────────────────────────────────────────
  const [envoyerDevisOpen,  setEnvoyerDevisOpen]  = useState(false);
  const [mailDevisAvenantId, setMailDevisAvenantId] = useState(null); // null = contrat principal, sinon avenant_id
  const [emailDevisOpen,    setEmailDevisOpen]    = useState(false);
  const [sendingEmailDevis, setSendingEmailDevis] = useState(false);
  const [emailContratSigneOpen,    setEmailContratSigneOpen]    = useState(false);
  const [sendingEmailContratSigne, setSendingEmailContratSigne] = useState(false);
  const envoyerDevisRef = useRef(null);

  // ── États du moteur de signature ─────────────────────────────────────────
  const [showSigModal, setShowSigModal]         = useState(false);
  const [sigMode, setSigMode]                   = useState(null); // 'tablette'|'ordinateur'|'mail'|'lien'|'recue'
  const [sigMenuOpen, setSigMenuOpen]           = useState(false);
  const [signaireNom, setSignaireNom]           = useState('');
  const [sigEmail, setSigEmail]                 = useState('');
  const [signingAvenantId, setSigningAvenantId] = useState(null); // null = signe le contrat, sinon signe l'avenant
  const [sigSujet, setSigSujet]                 = useState('');
  const [sigCorps, setSigCorps]                 = useState('');
  const [savingSig, setSavingSig]               = useState(false);
  const [sigHasDrawing, setSigHasDrawing]       = useState(false);
  const [sigContactSearch, setSigContactSearch] = useState('');
  const [sigContactOpen, setSigContactOpen]     = useState(false);
  const sigMenuRef    = useRef(null);
  const sigCanvasRef  = useRef(null);
  const sigLastPos    = useRef(null);
  const sigIsDrawing  = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    const agenceId = activeAgences?.length === 1 ? activeAgences[0].agence_id : undefined;
    try {
      const res = await api.getContratsV2({
        search: debouncedSearch || undefined, limit: 100,
        statut: filtreStatut.length ? filtreStatut.join(',') : undefined,
        statut_logistique: filtreLogistique.length ? filtreLogistique.join(',') : undefined,
        statut_facturation: filtreFacturation.length ? filtreFacturation.join(',') : undefined,
        livraisons: filtreLivraisons.length ? filtreLivraisons.join(',') : undefined,
        alertes: filtreAlertes.length ? filtreAlertes.join(',') : undefined,
        agence_id: agenceId,
      });
      setContrats(res.data.items || []);
    } catch (e) { toast.error('Erreur de chargement des contrats'); }
    finally { setLoading(false); }
  }, [debouncedSearch, filtreStatut, filtreLogistique, filtreFacturation, filtreLivraisons, filtreAlertes, activeAgences]);

  const loadKpis = useCallback(async () => {
    const agenceId = activeAgences?.length === 1 ? activeAgences[0].agence_id : undefined;
    try {
      const res = await api.getContratsV2Kpis({ search: debouncedSearch || undefined, agence_id: agenceId });
      setKpis(res.data);
    } catch (e) { /* silencieux : les cartes ne sont qu'indicatives */ }
  }, [debouncedSearch, activeAgences]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadKpis(); }, [loadKpis]);
  useEffect(() => { api.getLocationCategories().then(r => setCategories(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.getMotifsSuspension().then(r => setMotifsSuspension(r.data || [])).catch(() => {}); }, []);

  // Fermer le dropdown interlocuteur sur clic extérieur
  useEffect(() => {
    if (!contactDropOpen) return;
    const h = (e) => { if (contactDropRef.current && !contactDropRef.current.contains(e.target)) setContactDropOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [contactDropOpen]);

  // Fermer le menu signature sur clic extérieur
  useEffect(() => {
    if (!sigMenuOpen) return;
    const h = (e) => { if (sigMenuRef.current && !sigMenuRef.current.contains(e.target)) setSigMenuOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [sigMenuOpen]);

  // Fermer le dropdown "Envoyer le devis" sur clic extérieur
  useEffect(() => {
    if (!envoyerDevisOpen) return;
    const h = (e) => { if (envoyerDevisRef.current && !envoyerDevisRef.current.contains(e.target)) setEnvoyerDevisOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [envoyerDevisOpen]);

  const handleImprimerDevisV2 = async () => {
    setEnvoyerDevisOpen(false);
    try {
      const resp = await api.getContratV2PDF(editing.contrat_id);
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${editing.numero || editing.contrat_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch { toast.error('Erreur lors de la génération du PDF'); }
  };

  const openMailDevisModalV2 = (avenant = null) => {
    setEnvoyerDevisOpen(false);
    setMailDevisAvenantId(avenant ? avenant.avenant_id : null);
    setEmailDevisOpen(true);
  };

  const handleSendEmailDevisV2 = async ({ emails, sujet, corps }) => {
    const destinataire = emails[0];
    if (!destinataire) { return; }
    setSendingEmailDevis(true);
    try {
      if (mailDevisAvenantId) {
        const updatedAv = await api.envoyerEmailAvenantDevisV2(mailDevisAvenantId, { destinataire, sujet, corps });
        const extraEmails = emails.slice(1);
        for (const em of extraEmails) {
          await api.envoyerEmailAvenantDevisV2(mailDevisAvenantId, { destinataire: em, sujet, corps });
        }
        toast.success(`Devis envoyé à ${emails.join(', ')}`);
        setEmailDevisOpen(false);
        setAvenants(prev => prev.map(av => av.avenant_id === mailDevisAvenantId ? (updatedAv.data || updatedAv) : av));
      } else {
        const updated = await api.envoyerEmailContratDevisV2(editing.contrat_id, { destinataire, sujet, corps });
        const extraEmails = emails.slice(1);
        for (const em of extraEmails) {
          await api.envoyerEmailContratDevisV2(editing.contrat_id, { destinataire: em, sujet, corps });
        }
        toast.success(`Devis envoyé à ${emails.join(', ')}`);
        setEmailDevisOpen(false);
        setEditing(updated.data || updated);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi email');
    } finally { setSendingEmailDevis(false); }
  };

  useEffect(() => {
    if (!showSigModal) {
      setSigHasDrawing(false);
      setSigContactSearch('');
      setSigContactOpen(false);
      if (sigCanvasRef.current) sigCanvasRef.current.getContext('2d').clearRect(0, 0, 580, 200);
    }
  }, [showSigModal]);

  // ── Canvas signature ──────────────────────────────────────────────────────
  const getSigPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src  = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - rect.left) * (canvas.width / rect.width), y: (src.clientY - rect.top) * (canvas.height / rect.height) };
  };
  const sigStartDraw = (e) => { e.preventDefault(); sigIsDrawing.current = true; sigLastPos.current = getSigPos(e, sigCanvasRef.current); };
  const sigDraw = (e) => {
    e.preventDefault(); if (!sigIsDrawing.current) return;
    const canvas = sigCanvasRef.current; const ctx = canvas.getContext('2d'); const pos = getSigPos(e, canvas);
    ctx.beginPath(); ctx.moveTo(sigLastPos.current.x, sigLastPos.current.y); ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1A3A6E'; ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.stroke();
    sigLastPos.current = pos; setSigHasDrawing(true);
  };
  const sigStopDraw = () => { sigIsDrawing.current = false; };
  const sigClearCanvas = () => { if (sigCanvasRef.current) sigCanvasRef.current.getContext('2d').clearRect(0, 0, 580, 200); setSigHasDrawing(false); };

  // ── Handlers de signature ──────────────────────────────────────────────────
  const openSigMode = (mode, avenantId = null) => {
    setSigMode(mode); setSigMenuOpen(false); setSigningAvenantId(avenantId);
    // Pré-remplissage aligné V1 : nom, email, sujet et corps selon le mode
    const av    = avenantId ? avenants.find(a => a.avenant_id === avenantId) : null;
    const num   = av ? av.numero : (editing?.numero || '');
    const nom   = editing?.validation?.signataire_nom || editing?.contact_nom || '';
    const email = editing?.validation?.signataire_email || editing?.contact_email || editing?.client_email || '';
    setSignaireNom(nom);
    setSigEmail(email);
    if (mode === 'mail') {
      setSigSujet(`${num} — Signature requise`);
      setSigCorps(`Bonjour,\n\nVeuillez trouver ci-joint le contrat ${num} à signer.\n\nMerci de nous retourner le document signé par email dès que possible.\n\nCordialement,`);
    } else if (mode === 'lien' || mode === 'relien') {
      setSigSujet(`${num} — Signez en ligne`);
      setSigCorps(`Bonjour,\n\nVotre contrat ${num} est prêt à être signé.\n\nCliquez sur le bouton ci-dessous pour accéder à la page de signature en ligne.\n\nCordialement,`);
    }
    setShowSigModal(true);
  };

  const handleSignerTerrain = async () => {
    if (!sigHasDrawing || !signaireNom.trim()) return; setSavingSig(true);
    try {
      const payload = { signataire_nom: signaireNom, signature_image_b64: sigCanvasRef.current.toDataURL('image/png'), methode: sigMode };
      if (signingAvenantId) {
        await api.avenantSignerTerrain(signingAvenantId, payload);
        setShowSigModal(false); setSigningAvenantId(null); toast.success('Avenant signé');
        await refreshEditing();
      } else {
        const res = await api.signerContratTerrainV2(editing.contrat_id, payload);
        setEditing(res.data); setContrats(cs => cs.map(c => c.contrat_id === res.data.contrat_id ? res.data : c)); setShowSigModal(false); toast.success('Contrat signé');
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); } finally { setSavingSig(false); }
  };

  const handleEnvoyerMail = async () => {
    if (!signaireNom.trim() || !sigEmail.trim()) return; setSavingSig(true);
    try {
      const payload = { signataire_nom: signaireNom, signataire_email: sigEmail, sujet: sigSujet || null, corps: sigCorps || null };
      if (signingAvenantId) {
        await api.avenantEnvoiSignatureMail(signingAvenantId, payload);
        setShowSigModal(false); setSigningAvenantId(null); toast.success('Avenant envoyé par mail');
        await refreshEditing();
      } else {
        const res = await api.envoyerSignatureMailV2(editing.contrat_id, payload);
        setEditing(res.data); setContrats(cs => cs.map(c => c.contrat_id === res.data.contrat_id ? res.data : c)); setShowSigModal(false); toast.success('Contrat envoyé par mail');
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); } finally { setSavingSig(false); }
  };

  const handleEnvoyerLien = async () => {
    if (!signaireNom.trim() || !sigEmail.trim()) return; setSavingSig(true);
    try {
      const payload = { signataire_nom: signaireNom, signataire_email: sigEmail, sujet: sigSujet || null, corps: sigCorps || null };
      if (signingAvenantId) {
        await api.avenantEnvoiSignatureLien(signingAvenantId, payload);
        setShowSigModal(false); setSigningAvenantId(null); toast.success('Lien de signature envoyé');
        await refreshEditing();
      } else {
        const res = await api.envoyerSignatureLienV2(editing.contrat_id, payload);
        setEditing(res.data); setContrats(cs => cs.map(c => c.contrat_id === res.data.contrat_id ? res.data : c)); setShowSigModal(false); toast.success('Lien de signature envoyé');
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); } finally { setSavingSig(false); }
  };

  const handleSignatureRecue = async () => {
    setSavingSig(true);
    try {
      if (signingAvenantId) {
        await api.avenantSignatureRecue(signingAvenantId);
        setShowSigModal(false); setSigningAvenantId(null); toast.success('Signature avenant confirmée');
        await refreshEditing();
      } else {
        const res = await api.signatureRecueV2(editing.contrat_id, new FormData());
        setEditing(res.data); setContrats(cs => cs.map(c => c.contrat_id === res.data.contrat_id ? res.data : c)); setShowSigModal(false); toast.success('Signature confirmée');
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); } finally { setSavingSig(false); }
  };

  const handleAnnulerLien = async () => {
    if (!window.confirm('Révoquer ce lien ? Le client ne pourra plus l\'utiliser.')) return;
    try {
      const res = await api.annulerLienSignatureV2(editing.contrat_id);
      setEditing(res.data); setContrats(cs => cs.map(c => c.contrat_id === res.data.contrat_id ? res.data : c)); toast.success('Lien révoqué');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const copierLienSignature = () => {
    const token = editing?.validation?.signature_web_token;
    if (token) { navigator.clipboard.writeText(`${window.location.origin}/signer-v2/${token}`); toast.success('Lien copié'); }
  };

  // Héritage mode_reglement depuis la fiche client (parité V1)
  useEffect(() => {
    const clientId = form.client?.client_id;
    const clientType = form.client?.client_type;
    const contratId = editing?.contrat_id;
    const key = `${contratId ?? ''}|${clientId ?? ''}`;
    if (lastConditionsKeyRef.current === key && form.mode_reglement) return; // déjà chargé pour ce contrat+client
    if (!clientId) {
      setForm(f => ({ ...f, mode_reglement: '', conditions_paiement_nom: '' }));
      lastConditionsKeyRef.current = key;
      return;
    }
    lastConditionsKeyRef.current = key;
    if (clientType === 'personne') {
      setForm(f => ({ ...f, mode_reglement: 'comptant', conditions_paiement_nom: 'Paiement immédiat' }));
      return;
    }
    Promise.all([
      api.getEntreprise(clientId),
      api.getConditionsPaiement(),
    ]).then(([entRes, condRes]) => {
      const ent = entRes.data;
      if (!ent) return;
      checkSiren(ent);
      const mapping = condRes.data || [];
      let cpAgence = null;
      for (const ag of (activeAgences || [])) {
        const cpa = ent.conditions_paiement_par_agence?.[ag.agence_id];
        if (cpa?.id_odoo || cpa?.nom_condition) { cpAgence = cpa; break; }
      }
      if (cpAgence) {
        const mapped = mapping.find(c => c.id_odoo === cpAgence.id_odoo);
        setForm(f => ({
          ...f,
          mode_reglement: cpAgence.mode_reglement ?? mapped?.mode_reglement ?? 'comptant',
          conditions_paiement_nom: cpAgence.nom_condition ?? null,
        }));
      } else {
        setForm(f => ({ ...f, mode_reglement: 'comptant', conditions_paiement_nom: null }));
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.client?.client_id, form.client?.client_type, activeAgences, editing?.contrat_id]);

  useEffect(() => {
    const clientId = form.client?.client_id;
    const clientType = form.client?.client_type;
    if (!clientId || clientType !== 'entreprise') {
      setContacts([]);
      setContactsLoading(false);
      return;
    }
    setContactsLoading(true);
    api.getPersonnesEntreprise(clientId)
      .then(r => {
        const list = r.data?.items || r.data || [];
        setContacts(Array.isArray(list) ? list : []);
      })
      .catch(() => setContacts([]))
      .finally(() => setContactsLoading(false));
  }, [form.client?.client_id, form.client?.client_type]);

  const loadGammes = async (categorieId) => {
    if (!categorieId || gammesByCat[categorieId]) return;
    try {
      const res = await api.getLocationGammes({ categorie_id: categorieId });
      setGammesByCat(prev => ({ ...prev, [categorieId]: res.data || [] }));
    } catch { /* ignore */ }
  };

  const loadBls = useCallback(async (contratId) => {
    setLoadingBls(true);
    try {
      const res = await api.getBLsV2(contratId);
      setBls(res.data || []);
    } catch (e) { toast.error('Erreur de chargement des mouvements'); }
    finally { setLoadingBls(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'terrain' && editing) loadBls(editing.contrat_id);
  }, [activeTab, editing, loadBls]);

  // Panel brouillon Pennylane (parité V1 / Ventes / Réparation)
  const [plDraftPanel, setPlDraftPanel] = useState(null); // null | { loading, pdf_blob_url }
  const [activePLFac, setActivePLFac] = useState(null);
  const [validantPL, setValidantPL] = useState(false);
  const [supprimantDraftPL, setSupprimantDraftPL] = useState(false);
  const [showReglPLPanel, setShowReglPLPanel] = useState(false);
  const [reglPLSaisi, setReglPLSaisi] = useState(null);
  // Facture acquittée + envoi email (parité V1)
  const [downloadingAcquittee, setDownloadingAcquittee] = useState(false);
  const [factureEmailPanel, setFactureEmailPanel] = useState({ open: false, loading: false, destinataires: [] });
  const [envoyantFactureEmail, setEnvoyantFactureEmail] = useState(false);
  const { user } = useAuth();

  const loadFactures = useCallback(async (contratId) => {
    setLoadingFactures(true);
    try {
      const res = await api.getFacturesV2(contratId);
      setFactures(res.data || []);
    } catch (e) { toast.error('Erreur de chargement des factures'); }
    finally { setLoadingFactures(false); }
  }, []);

  // ── Panel brouillon Pennylane (parité V1) ───────────────────────────────
  const openPlDraftPanel = async (fac) => {
    setActivePLFac(fac);
    setPlDraftPanel({ loading: true, pdf_blob_url: null });
    const reglProm = api.getReglementsFactureLoc(fac.facture_id).then(r => r.data).catch(() => null);
    try {
      const [resp, reglData] = await Promise.all([
        api.getPlFactureLocPdfBlob(fac.facture_id),
        reglProm,
      ]);
      const blobUrl = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      setPlDraftPanel({ loading: false, pdf_blob_url: blobUrl });
      if (reglData?.reglements?.length > 0) setReglPLSaisi(reglData);
    } catch (e) {
      const detail = e.response?.data instanceof Blob ? null : (e.response?.data?.detail || null);
      if (detail) toast.error(detail);
      setPlDraftPanel({ loading: false, pdf_blob_url: null });
    }
  };

  const closePlDraftPanel = () => {
    if (plDraftPanel?.pdf_blob_url) URL.revokeObjectURL(plDraftPanel.pdf_blob_url);
    setPlDraftPanel(null);
    setShowReglPLPanel(false);
    setReglPLSaisi(null);
    setActivePLFac(null);
  };

  const handleValiderPL = async () => {
    if (!activePLFac) return;
    setValidantPL(true);
    try {
      await api.validerPlFactureLocV2(activePLFac.facture_id);
      toast.success('Facture validée dans Pennylane');
      closePlDraftPanel();
      if (editing) loadFactures(editing.contrat_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors de la validation'); }
    finally { setValidantPL(false); }
  };

  const handleSupprimerDraftPL = async () => {
    if (!activePLFac) return;
    setSupprimantDraftPL(true);
    try {
      await api.supprimerPlDraftLoc(activePLFac.facture_id);
      toast.success('Brouillon supprimé — la période redevient facturable');
      closePlDraftPanel();
      if (editing) loadFactures(editing.contrat_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors de la suppression'); }
    finally { setSupprimantDraftPL(false); }
  };

  // ── Facture acquittée (parité V1) ─────────────────────────────────────
  const handleAcquittee = async () => {
    if (!activePLFac) return;
    setDownloadingAcquittee(true);
    try {
      const res = await api.getFactureAcquitteeBlob(activePLFac.facture_id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `facture-acquittee-${(activePLFac.ext_invoice_number || activePLFac.facture_id).replace(/\//g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      let msg = 'Erreur lors de la génération de la facture acquittée';
      try {
        const d = e?.response?.data;
        if (d instanceof Blob) { const t = await d.text(); msg = JSON.parse(t)?.detail || msg; }
        else if (d?.detail) msg = d.detail;
      } catch {}
      toast.error(msg);
    } finally {
      setDownloadingAcquittee(false);
    }
  };

  // ── Envoi facture par email (parité V1) ───────────────────────────────
  const handleEnvoyerFactureEmail = async () => {
    if (!editing) return;
    setFactureEmailPanel({ open: true, loading: true, destinataires: [] });
    try {
      const r = await api.getDestinatairesFactureV2(editing.contrat_id);
      setFactureEmailPanel(p => ({ ...p, loading: false, destinataires: r.data?.destinataires || [], factureMeta: r.data?.facture || null }));
    } catch (e) {
      setFactureEmailPanel(p => ({ ...p, loading: false }));
      toast.error('Impossible de charger les destinataires');
    }
  };

  const handleConfirmFactureEmail = async ({ emails, recipients, sujet, corps }) => {
    if (!editing) return;
    setEnvoyantFactureEmail(true);
    try {
      const r = await api.envoyerFactureEmailV2(editing.contrat_id, { to_emails: emails, recipients, sujet, corps });
      toast.success(r.data?.message || `Email(s) envoyé(s) à ${emails.length} destinataire(s)`);
      setFactureEmailPanel({ open: false, loading: false, destinataires: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setEnvoyantFactureEmail(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'facturation' && editing) { loadFactures(editing.contrat_id); setPreviewFacture(null); setFacturationPeriodeFin(''); setFacturationRefClient(''); }
  }, [activeTab, editing, loadFactures]);

  const previewFacturation = async () => {
    setLoadingPreview(true);
    setPreviewFacture(null);
    try {
      const res = await api.facturerContratV2(editing.contrat_id, {
        periode_fin: facturationPeriodeFin || undefined, dry_run: true,
      });
      setPreviewFacture(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rien à facturer sur cette période');
    } finally { setLoadingPreview(false); }
  };

  const confirmFacturation = async () => {
    if (!previewFacture) return;
    setConfirmingFacture(true);
    try {
      const res = await api.facturerContratV2(editing.contrat_id, {
        periode_debut: previewFacture.premier_jour_facturable || previewFacture.periode_debut,
        periode_fin: previewFacture.periode_fin,
        ref_client: facturationRefClient.trim() || undefined,
        dry_run: false,
      });
      toast.success(`Facture ${res.data.facture_id} créée — ${fmtEur(res.data.total_ttc)} TTC`);
      setPreviewFacture(null);
      setFacturationRefClient('');
      loadFactures(editing.contrat_id);
      refreshEditing();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la facturation');
    } finally { setConfirmingFacture(false); }
  };

  const sortiesParLigne = useMemo(() => {
    const map = {};
    const sorted = [...bls].sort((a, b) => new Date(a.date_prevue) - new Date(b.date_prevue));
    for (const bl of sorted) {
      for (const l of bl.lignes || []) {
        if (!map[l.ligne_contrat_id]) map[l.ligne_contrat_id] = {};
        const cur = map[l.ligne_contrat_id][l.equipement_id] || { count: 0 };
        cur.count += bl.type_mouvement === 'SORTIE' ? 1 : -1;
        cur.nom = l.equipement_nom;
        cur.numero_serie = l.numero_serie;
        map[l.ligne_contrat_id][l.equipement_id] = cur;
      }
    }
    const result = {};
    for (const ligneId of Object.keys(map)) {
      result[ligneId] = Object.entries(map[ligneId])
        .filter(([, v]) => v.count > 0)
        .map(([eid, v]) => ({ equipement_id: eid, nom: v.nom, numero_serie: v.numero_serie }));
    }
    return result;
  }, [bls]);

  // Unités déjà planifiées en sortie (BL SORTIE en statut A_LIVRER) par ligne de contrat
  const pendingSortiesParLigne = useMemo(() => {
    const map = {};
    for (const bl of bls) {
      if (bl.type_mouvement !== 'SORTIE' || bl.statut !== 'A_LIVRER') continue;
      for (const l of bl.lignes || []) {
        map[l.ligne_contrat_id] = (map[l.ligne_contrat_id] || 0) + 1;
      }
    }
    return map;
  }, [bls]);

  // Pré-charge les matériels disponibles pour toutes les lignes actives dès l'ouverture de l'onglet terrain
  useEffect(() => {
    if (activeTab !== 'terrain' || !editing) return;
    const lignes = (editing.lignes || []).filter(l => l.statut_ligne === 'active');
    // Pré-remplir la date avec date_debut_prevue de chaque ligne (fallback: date_debut_initiale du contrat, puis aujourd'hui)
    // Heure proposée : 07h00, heure de l'agence (fuseau de la société active)
    setBlSelDate(prev => {
      const next = { ...prev };
      lignes.forEach(l => {
        if (!next[l.ligne_id]) {
          const src = l.date_debut_prevue || editing.date_debut_initiale;
          next[l.ligne_id] = src
            ? src.slice(0, 10) + 'T07:00'
            : todayInTz(activeSocieteTimezone) + 'T07:00';
        }
      });
      return next;
    });
    lignes.forEach(l => {
      if (!materielsDispo[l.ligne_id]) {
        api.getMaterielsDisponiblesV2(editing.contrat_id, l.ligne_id)
          .then(res => setMaterielsDispo(p => ({ ...p, [l.ligne_id]: res.data || [] })))
          .catch(() => {});
      }
    });
  }, [activeTab, editing?.contrat_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePreparerLigne = async (ligneId, typeMouvement, equipementId, dateOverride = null) => {
    if (!equipementId) { toast.error('Sélectionnez un matériel'); return; }
    setSavingBl(true);
    try {
      const rawDate = dateOverride || blSelDate[ligneId];
      await api.preparerBLV2(editing.contrat_id, {
        type_mouvement: typeMouvement,
        date_prevue: rawDate ? wallTimeInTzToIso(rawDate, activeSocieteTimezone) : null,
        lignes: [{ ligne_contrat_id: ligneId, equipement_id: equipementId }],
      });
      toast.success(typeMouvement === 'SORTIE' ? 'Sortie préparée — visible sur tablette' : 'Retour préparé — visible sur tablette');
      setBlSel(p => ({ ...p, [ligneId]: '' }));
      loadBls(editing.contrat_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur préparation BL');
    } finally { setSavingBl(false); }
  };

  const handleReserver = async (ligneId, equipementId) => {
    setReservingLine(ligneId);
    try {
      await api.reserverEquipementV2(editing.contrat_id, ligneId, { equipement_id: equipementId || null });
      toast.success(equipementId ? 'Matériel réservé sur le planning' : 'Réservation libérée');
      // Recharger le contrat pour avoir equipement_id_reserve à jour dans editing.lignes
      const res = await api.getContratV2(editing.contrat_id);
      setEditing(res.data);
      // Rafraîchir la liste dispo pour la ligne (le matériel réservé ne doit plus apparaître)
      api.getMaterielsDisponiblesV2(editing.contrat_id, ligneId)
        .then(r => setMaterielsDispo(p => ({ ...p, [ligneId]: r.data || [] })))
        .catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur réservation');
    } finally { setReservingLine(null); }
  };

  const loadAvenants = useCallback(async (contratId) => {
    setLoadingAvenants(true);
    try {
      const res = await api.getAvenantsV2(contratId);
      setAvenants(res.data || []);
    } catch (e) { toast.error('Erreur de chargement des avenants'); }
    finally { setLoadingAvenants(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'avenants' && editing) loadAvenants(editing.contrat_id);
  }, [activeTab, editing, loadAvenants]);

  const submitAvenant = async () => {
    const f = avenantForm;
    if (!f.date_effet) { toast.error("Date d'effet requise"); return; }
    const body = { type_avenant: f.type_avenant, motif: f.motif || null, date_effet: f.date_effet };

    if (f.type_avenant === 'PROLONGATION') {
      if (!f.ligne_id || !f.nouvelle_date_fin) { toast.error('Ligne et nouvelle date de fin requises'); return; }
      body.ligne_id = f.ligne_id;
      body.nouvelle_date_fin = f.nouvelle_date_fin;
    } else if (f.type_avenant === 'AJUSTEMENT_TARIF') {
      if (!f.ligne_id || f.nouveau_prix_jour === '') { toast.error('Ligne et nouveau prix requis'); return; }
      body.ligne_id = f.ligne_id;
      body.nouveau_prix_jour = Number(f.nouveau_prix_jour);
    } else if (f.type_avenant === 'RETRAIT_MATERIEL') {
      if (!f.ligne_id || f.nouvelle_quantite === '') { toast.error('Ligne et nouvelle quantité requises'); return; }
      body.ligne_id = f.ligne_id;
      body.nouvelle_quantite = Number(f.nouvelle_quantite);
    } else if (f.type_avenant === 'AJOUT_MATERIEL') {
      if (f.ajoutMode === 'existante') {
        if (!f.ligne_id || f.nouvelle_quantite === '') { toast.error('Ligne et nouvelle quantité requises'); return; }
        body.ligne_id = f.ligne_id;
        body.nouvelle_quantite = Number(f.nouvelle_quantite);
      } else {
        if (!f.nouvelle_categorie_id || !f.nouvelle_gamme_id || !f.nouvelle_ligne_date_debut || !f.nouvelle_ligne_date_fin) {
          toast.error('Catégorie, gamme et dates requises'); return;
        }
        const cat = categories.find(c => c.categorie_id === f.nouvelle_categorie_id);
        const gamme = (gammesByCat[f.nouvelle_categorie_id] || []).find(g => g.gamme_id === f.nouvelle_gamme_id);
        body.nouvelle_ligne = {
          categorie_id: f.nouvelle_categorie_id, categorie_nom: cat?.nom || null,
          gamme_id: f.nouvelle_gamme_id, gamme_nom: gamme?.nom || null,
          quantite: Number(f.nouvelle_ligne_quantite) || 1,
          date_debut_prevue: f.nouvelle_ligne_date_debut, date_fin_prevue: f.nouvelle_ligne_date_fin,
          prix_unitaire_jour: f.nouvelle_ligne_prix !== '' ? Number(f.nouvelle_ligne_prix) : null,
        };
      }
    }

    setSavingAvenant(true);
    try {
      await api.createAvenantV2(editing.contrat_id, body);
      toast.success('Avenant créé en brouillon');
      setAvenantForm(null);
      loadAvenants(editing.contrat_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de la création de l'avenant"); }
    finally { setSavingAvenant(false); }
  };

  const annulerAvenant = async (avenantId) => {
    try {
      await api.annulerAvenantV2(avenantId);
      toast.success('Avenant annulé');
      loadAvenants(editing.contrat_id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Annulation impossible'); }
  };

  const loadSuspensions = useCallback(async (contratId) => {
    setLoadingSuspensions(true);
    try {
      const res = await api.getSuspensionsV2(contratId);
      setSuspensions(res.data || []);
    } catch (e) { toast.error('Erreur de chargement des suspensions'); }
    finally { setLoadingSuspensions(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'suspensions' && editing) loadSuspensions(editing.contrat_id);
  }, [activeTab, editing, loadSuspensions]);

  const submitSuspension = async () => {
    const f = suspensionForm;
    if (!f.ligne_contrat_id || !f.motif_id || !f.date_debut || !f.date_fin) {
      toast.error('Ligne, motif et dates requis'); return;
    }
    setSavingSuspension(true);
    try {
      await api.createSuspensionV2(editing.contrat_id, {
        ligne_contrat_id: f.ligne_contrat_id, motif_id: f.motif_id,
        date_debut: f.date_debut, date_fin: f.date_fin,
        taux_facturation: Number(f.taux_facturation) || 0, description: f.description || null,
      });
      toast.success('Suspension enregistrée');
      setSuspensionForm(null);
      loadSuspensions(editing.contrat_id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur lors de la création de la suspension'); }
    finally { setSavingSuspension(false); }
  };

  const deleteSuspension = async (suspensionId) => {
    try {
      await api.deleteSuspensionV2(suspensionId);
      toast.success('Suspension supprimée');
      loadSuspensions(editing.contrat_id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Suppression impossible'); }
  };

  const refreshEditing = async () => {
    const res = await api.getContratV2(editing.contrat_id);
    const doc = res.data;
    setEditing(doc);
    setLocalServices(doc.services || []);
    const pendingIds = (['signe', 'en_cours'].includes(doc.statut) && doc.date_signature)
      ? (doc.services || [])
          .filter(s => !s.avenant_id && new Date(s.created_at) > new Date(doc.date_signature))
          .map(s => s.service_id)
      : [];
    setPendingServiceIds(new Set(pendingIds));
    setInitialServices((doc.services || []).filter(s => !pendingIds.includes(s.service_id)));
    return doc;
  };

  const deleteService = async (serviceId) => {
    // Ligne locale (ajout non encore validé par avenant) → simple retrait local
    if (serviceId.startsWith('LOCALV2-')) {
      setLocalServices(prev => prev.filter(s => s.service_id !== serviceId));
      return;
    }
    try {
      await api.deleteServiceV2(serviceId);
      toast.success('Service supprimé');
      await refreshEditing();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Suppression impossible'); }
  };

  // Champs qui partent dans l'avenant groupé quand le contrat est verrouillé
  const AVENANT_SVC_FIELDS = ['designation', 'quantite', 'prix_unitaire_ht', 'taux_tva'];
  const patchServiceField = async (serviceId, field, value) => {
    // Mise à jour optimiste locale + patch backend
    setLocalServices(prev => prev.map(s => s.service_id === serviceId ? { ...s, [field]: value } : s));
    // Ligne locale (ajout non encore validé par avenant) → rien en base à patcher
    if (serviceId.startsWith('LOCALV2-')) return;
    // Contrat verrouillé + service déjà formalisé → la modification partira dans
    // l'avenant groupé (« Valider par un avenant »), pas de patch immédiat.
    // Exception : contrat terminé → on autorise l'édition locale même pour les services facturés.
    const isTermine = editing?.statut === 'termine';
    if (isFinancialLocked && !pendingServiceIds.has(serviceId) && AVENANT_SVC_FIELDS.includes(field) && !isTermine) return;
    try { await api.patchServiceV2(serviceId, { [field]: value }); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur de mise à jour'); await refreshEditing(); }
  };

  const submitRetraitService = async () => {
    const f = retraitServiceForm;
    if (!f.date_effet) { toast.error("Date d'effet requise"); return; }
    setSavingRetraitService(true);
    try {
      await api.createAvenantV2(editing.contrat_id, {
        type_avenant: 'RETRAIT_SERVICE', date_effet: f.date_effet, motif: f.motif || null, service_id: f.service_id,
      });
      toast.success("Avenant créé en brouillon — validez-le dans l'onglet Avenants");
      setRetraitServiceForm(null);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur lors du retrait du service'); }
    finally { setSavingRetraitService(false); }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    initialClientIdV2.current = null;
    setLignes([emptyLigne()]);
    setInitialLignes([]);
    setActiveTab('info');
    setBls([]);
    setAvenants([]);
    setAvenantForm(null);
    setSuspensions([]);
    setSuspensionForm(null);
    setRetraitServiceForm(null);
    setPanelOpen(true);
  };

  const openEdit = async (contrat) => {
    try {
      lastConditionsKeyRef.current = null; // forcer rechargement des conditions de règlement
      const res = await api.getContratV2(contrat.contrat_id);
      const doc = res.data;
      setEditing(doc);
      setActiveTab('info');
      setBls([]);
      setAvenants([]);
      setAvenantForm(null);
      setSuspensions([]);
      setSuspensionForm(null);
      setRetraitServiceForm(null);
      setLocalServices(doc.services || []);
      // Peupler les services "en attente de formalisation" :
      // services sans avenant_id créés APRÈS la signature du contrat
      const pendingIds = (['signe', 'en_cours'].includes(doc.statut) && doc.date_signature)
        ? (doc.services || [])
            .filter(s => !s.avenant_id && new Date(s.created_at) > new Date(doc.date_signature))
            .map(s => s.service_id)
        : [];
      setPendingServiceIds(new Set(pendingIds));
      setInitialServices((doc.services || []).filter(s => !pendingIds.includes(s.service_id)));
      setForm({
        client: { client_id: doc.client_id, client_type: doc.client_type, client_nom: doc.client_nom, tiers_id: doc.tiers_id },
        contact_id: doc.contact_id || null, contact_nom: doc.contact_nom || null,
        commercial_id: doc.commercial_id || null, commercial_nom: doc.commercial_nom || null,
        titre: doc.titre || '', ref_client: doc.ref_client || '', adresse_livraison: doc.adresse_livraison || '', mode_reglement: doc.mode_reglement || '', conditions_paiement_nom: doc.conditions_paiement_nom || '', notes: doc.notes || '',
      });
      initialClientIdV2.current = doc.client_id;
      const ll = (doc.lignes || []).filter(l => l.statut_ligne === 'active').map(l => ({
        ligne_id: l.ligne_id, categorie_id: l.categorie_id, categorie_nom: l.categorie_nom,
        gamme_id: l.gamme_id, gamme_nom: l.gamme_nom, quantite: l.quantite,
        date_debut_prevue: l.date_debut_prevue, date_fin_prevue: l.date_fin_prevue,
        prix_unitaire_jour: l.prix_unitaire_jour ?? '',
        prix_est_forfait: false,
        jours_exclus: Array.isArray(l.jours_exclus) ? l.jours_exclus : [5, 6],
        note: l.note || '',
      }));
      setLignes(ll.length ? ll : [emptyLigne()]);
      setInitialLignes(ll);
      ll.forEach(l => l.categorie_id && loadGammes(l.categorie_id));
      setPanelOpen(true);
    } catch (e) { toast.error('Erreur de chargement du contrat'); }
  };

  // Auto-ouverture du panneau si ?contrat_id=xxx dans l'URL (lien mail confirmation signature — parité V1)
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const cid = searchParams.get('contrat_id');
    if (cid) {
      api.getContratV2(cid)
        .then(r => { if (r.data) openEdit(r.data); })
        .catch(() => {});
      return;
    }
    // Pré-remplissage depuis le planning (clic sur une cellule)
    if (searchParams.get('new') === '1') {
      const date_debut = searchParams.get('date_debut') || '';
      const gamme_id   = searchParams.get('gamme_id')   || '';
      const gamme_nom  = searchParams.get('gamme_nom')  || '';
      const firstLigne = {
        ...emptyLigne(),
        date_debut_prevue: date_debut,
        date_fin_prevue:   date_debut,
        ...(gamme_id && gamme_id !== '_no_gamme' ? { gamme_id, gamme_nom } : {}),
      };
      setEditing(null);
      setForm(emptyForm());
      initialClientIdV2.current = null;
      setLignes([firstLigne]);
      setInitialLignes([]);
      setActiveTab('info');
      setBls([]); setAvenants([]); setAvenantForm(null);
      setSuspensions([]); setSuspensionForm(null); setRetraitServiceForm(null);
      setPanelOpen(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAnnulerContrat = async () => {
    const isPostLivraison = ['en_cours', 'retour', 'termine'].includes(editing?.statut);
    const msg = isPostLivraison
      ? `ANNULATION APRÈS LIVRAISON — Opération irréversible.\n\nConséquences :\n• Tous les BLs seront annulés\n• Les matériels seront remis en disponible\n• Les réservations seront libérées\n\nImpossible si une facture a été émise.\n\nConfirmer l'annulation de force ?`
      : `Annuler ce contrat ? Cette opération est irréversible.`;
    if (!window.confirm(msg)) return;
    try {
      await api.annulerContratV2(editing.contrat_id, { force: isPostLivraison });
      toast.success('Contrat annulé');
      setPanelOpen(false);
      loadKpis();
      if (filtreStatut.length || filtreLogistique.length || filtreFacturation.length || filtreLivraisons.length || filtreAlertes.length) {
        setFiltreStatut([]);
        setFiltreLogistique([]);
        setFiltreFacturation([]);
        setFiltreLivraisons([]);
        setFiltreAlertes([]);
      } else {
        load();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur lors de l\'annulation'); }
  };

  const handleRemettreEnBrouillon = async () => {
    try {
      const res = await api.remettreEnBrouillonV2(editing.contrat_id);
      toast.success('Contrat remis en brouillon');
      setEditing(res.data);
      loadKpis();
      if (filtreStatut || filtreLogistique || filtreFacturation) {
        setFiltreStatut(null);
        setFiltreLogistique(null);
        setFiltreFacturation(null);
      } else {
        load();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const updateLigne = (idx, patch) => setLignes(prev => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));

  // ── Calcul du diff lignes entre l'état local et l'état initial ───────────────
  const computeLigneDiff = () => {
    const initialIds = new Set(initialLignes.map(l => l.ligne_id).filter(Boolean));
    const currentIds = new Set(lignes.map(l => l.ligne_id).filter(Boolean));

    const lignes_ajoutees = lignes.filter(l => !l.ligne_id); // nouvelles lignes sans ID
    const lignes_retirees = [...initialIds].filter(id => !currentIds.has(id));
    const lignes_modifiees = lignes.filter(l => {
      if (!l.ligne_id) return false;
      const orig = initialLignes.find(o => o.ligne_id === l.ligne_id);
      if (!orig) return false;
      const norm = (x) => JSON.stringify({
        categorie_id: x.categorie_id || '', gamme_id: x.gamme_id || '',
        quantite: Number(x.quantite) || 1,
        date_debut_prevue: x.date_debut_prevue || '', date_fin_prevue: x.date_fin_prevue || '',
        prix_unitaire_jour: (x.prix_unitaire_jour === '' || x.prix_unitaire_jour == null) ? null : Number(x.prix_unitaire_jour),
        jours_exclus: [...(Array.isArray(x.jours_exclus) ? x.jours_exclus : [5, 6])].map(Number).sort((a, b) => a - b),
        note: x.note || '',
      });
      return norm(orig) !== norm(l);
    });
    return { lignes_ajoutees, lignes_modifiees, lignes_retirees };
  };

  const hasLigneChanges = (() => {
    const { lignes_ajoutees, lignes_modifiees, lignes_retirees } = computeLigneDiff();
    return lignes_ajoutees.length > 0 || lignes_modifiees.length > 0 || lignes_retirees.length > 0;
  })();

  const handleValiderSansAvenant = async () => {
    const { lignes_modifiees } = computeLigneDiff();
    setSavingSession(true);
    try {
      // 1. Lignes modifiées → PATCH direct (sans avenant)
      for (const l of lignes_modifiees) {
        await api.updateLigneV2(l.ligne_id, {
          categorie_id: l.categorie_id, categorie_nom: l.categorie_nom,
          gamme_id: l.gamme_id, gamme_nom: l.gamme_nom,
          quantite: Number(l.quantite) || 1,
          date_debut_prevue: l.date_debut_prevue, date_fin_prevue: l.date_fin_prevue,
          prix_unitaire_jour: l.prix_unitaire_jour ? Number(l.prix_unitaire_jour) : null,
          mode_calcul: l.mode_calcul || '5j/7', exclure_feries: l.exclure_feries ?? true,
          jours_exclus: l.jours_exclus || [5, 6], note: l.note || null,
        });
      }
      // 2. Services avec modifications locales → PATCH direct
      for (const svc of svcModifies) {
        await api.patchServiceV2(svc.service_id, {
          designation: svc.designation,
          quantite: Number(svc.quantite) || 0,
          prix_unitaire_ht: Number(svc.prix_unitaire_ht) || 0,
          taux_tva: Number(svc.taux_tva ?? 20),
          mode_facturation: svc.mode_facturation,
        });
      }
      // 3. Nouveaux services locaux → création directe
      for (const s of newServicesLocal) {
        if (!s.service_id?.startsWith('LOCALV2-')) continue;
        await api.createServiceV2(editing.contrat_id, {
          article_id: s.article_id || null,
          designation: s.designation || '',
          type_ligne: s.type_ligne || 'prestation',
          quantite: Number(s.quantite) || 1,
          prix_unitaire_ht: Number(s.prix_unitaire_ht) || 0,
          taux_tva: s.taux_tva != null ? Number(s.taux_tva) : null,
          mode_facturation: s.mode_facturation || 'FORFAIT_UNIQUE',
        });
      }
      toast.success('Modifications enregistrées directement');
      setPendingServiceIds(new Set());
      // Réinitialiser les services locaux LOCALV2 (ils ont été créés en base)
      setLocalServices(prev => prev.filter(s => !s.service_id?.startsWith('LOCALV2-')));
      const freshDoc = await refreshEditing();
      const ll = (freshDoc.lignes || []).filter(l => l.statut_ligne === 'active').map(l => ({
        ligne_id: l.ligne_id, categorie_id: l.categorie_id, categorie_nom: l.categorie_nom,
        gamme_id: l.gamme_id, gamme_nom: l.gamme_nom, quantite: l.quantite,
        date_debut_prevue: l.date_debut_prevue, date_fin_prevue: l.date_fin_prevue,
        prix_unitaire_jour: l.prix_unitaire_jour ?? '',
        prix_est_forfait: false,
        jours_exclus: Array.isArray(l.jours_exclus) ? l.jours_exclus : [5, 6],
        note: l.note || '',
      }));
      setLignes(ll.length ? ll : [emptyLigne()]);
      setInitialLignes(ll);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la correction directe');
    } finally { setSavingSession(false); }
  };

  const handleValiderSession = async () => {
    const today = new Date().toISOString().slice(0, 10);
    const { lignes_ajoutees, lignes_modifiees, lignes_retirees } = computeLigneDiff();
    setSavingSession(true);
    try {
      const res = await api.createAvenantSessionV2(editing.contrat_id, {
        date_effet: today,
        motif: null,
        lignes_ajoutees: lignes_ajoutees.map(l => ({
          categorie_id: l.categorie_id, categorie_nom: l.categorie_nom,
          gamme_id: l.gamme_id, gamme_nom: l.gamme_nom, quantite: Number(l.quantite) || 1,
          date_debut_prevue: l.date_debut_prevue, date_fin_prevue: l.date_fin_prevue,
          prix_unitaire_jour: l.prix_unitaire_jour ? Number(l.prix_unitaire_jour) : null,
          mode_calcul: l.mode_calcul || '5j/7', exclure_feries: l.exclure_feries ?? true,
          jours_exclus: l.jours_exclus || [5, 6], note: l.note || null,
        })),
        lignes_modifiees: lignes_modifiees.map(l => ({
          ligne_id: l.ligne_id, categorie_id: l.categorie_id, categorie_nom: l.categorie_nom,
          gamme_id: l.gamme_id, gamme_nom: l.gamme_nom, quantite: Number(l.quantite) || 1,
          date_debut_prevue: l.date_debut_prevue, date_fin_prevue: l.date_fin_prevue,
          prix_unitaire_jour: l.prix_unitaire_jour ? Number(l.prix_unitaire_jour) : null,
          mode_calcul: l.mode_calcul || '5j/7', exclure_feries: l.exclure_feries ?? true,
          jours_exclus: l.jours_exclus || [5, 6], note: l.note || null,
        })),
        lignes_retirees,
        services_pending_ids: [...pendingServiceIds],
        services_modifies: svcModifies.map(s => ({
          service_id: s.service_id,
          designation: s.designation || '',
          quantite: Number(s.quantite) || 0,
          prix_unitaire_ht: Number(s.prix_unitaire_ht) || 0,
          taux_tva: Number(s.taux_tva ?? 20),
        })),
        services_ajoutes: newServicesLocal.map(s => ({
          article_id: s.article_id || null,
          designation: s.designation || '',
          type_ligne: s.type_ligne || 'prestation',
          quantite: Number(s.quantite) || 1,
          prix_unitaire_ht: Number(s.prix_unitaire_ht) || 0,
          taux_tva: s.taux_tva != null ? Number(s.taux_tva) : null,
          mode_facturation: s.mode_facturation || 'FORFAIT_UNIQUE',
        })),
      });
      const avenant = res.data;
      setPendingServiceIds(new Set());
      // Ouvrir le menu de signature ciblé sur l'avenant (même flow que "Envoyer en signature")
      setSigMenuOpen(true);
      setSigningAvenantId(avenant.avenant_id);
      const freshDoc = await refreshEditing();
      // Resynchroniser les lignes locales avec l'état serveur (lignes remplacées/ajoutées
      // par l'avenant) — sinon le diff reste actif et un 2e clic recrée les lignes en doublon
      const ll = (freshDoc.lignes || []).filter(l => l.statut_ligne === 'active').map(l => ({
        ligne_id: l.ligne_id, categorie_id: l.categorie_id, categorie_nom: l.categorie_nom,
        gamme_id: l.gamme_id, gamme_nom: l.gamme_nom, quantite: l.quantite,
        date_debut_prevue: l.date_debut_prevue, date_fin_prevue: l.date_fin_prevue,
        prix_unitaire_jour: l.prix_unitaire_jour ?? '',
        prix_est_forfait: false,
        jours_exclus: Array.isArray(l.jours_exclus) ? l.jours_exclus : [5, 6],
        note: l.note || '',
      }));
      setLignes(ll.length ? ll : [emptyLigne()]);
      setInitialLignes(ll);
      ll.forEach(l => l.categorie_id && loadGammes(l.categorie_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la création de l\'avenant');
    } finally { setSavingSession(false); }
  };
  const addLigneRow = () => setLignes(prev => [...prev, emptyLigne()]);
  const removeLigneRow = (idx) => setLignes(prev => prev.filter((_, i) => i !== idx));

  const onCategorieChange = (idx, categorieId) => {
    const cat = categories.find(c => c.categorie_id === categorieId);
    updateLigne(idx, { categorie_id: categorieId, categorie_nom: cat?.nom || '', gamme_id: '', gamme_nom: '' });
    loadGammes(categorieId);
  };

  const onGammeChange = (idx, gammeId) => {
    const cat = lignes[idx].categorie_id;
    const gamme = (gammesByCat[cat] || []).find(g => g.gamme_id === gammeId);
    const joursExclus = lignes[idx].jours_exclus || [5, 6];
    const { date_debut_prevue, date_fin_prevue, prix_est_forfait } = lignes[idx];
    let prix = lignes[idx].prix_unitaire_jour;
    // On recalcule depuis la grille seulement si pas en mode forfait
    if (!prix_est_forfait && gamme?.grille_tarifaire?.length && date_debut_prevue && date_fin_prevue) {
      const duree = calcDureeWorkingDays(date_debut_prevue, date_fin_prevue, joursExclus);
      const autoPrix = getPrixJourV2(duree, gamme.grille_tarifaire);
      if (autoPrix != null) prix = String(autoPrix);
    }
    updateLigne(idx, { gamme_id: gammeId, gamme_nom: gamme?.nom || '', prix_unitaire_jour: prix });
  };

  const onDateChange = (idx, field, value) => {
    const updated = { ...lignes[idx], [field]: value };
    if (!updated.prix_est_forfait && updated.gamme_id && updated.date_debut_prevue && updated.date_fin_prevue) {
      const gamme = (gammesByCat[updated.categorie_id] || []).find(g => g.gamme_id === updated.gamme_id);
      if (gamme?.grille_tarifaire?.length) {
        const duree = calcDureeWorkingDays(updated.date_debut_prevue, updated.date_fin_prevue, updated.jours_exclus || [5, 6]);
        const autoPrix = getPrixJourV2(duree, gamme.grille_tarifaire);
        if (autoPrix != null) { updateLigne(idx, { [field]: value, prix_unitaire_jour: String(autoPrix) }); return; }
      }
    }
    updateLigne(idx, { [field]: value });
  };

  const onJoursExclusToggle = (idx, dayIdx) => {
    const current = lignes[idx].jours_exclus || [5, 6];
    const newExclus = current.includes(dayIdx)
      ? current.filter(d => d !== dayIdx)
      : [...current, dayIdx].sort((a, b) => a - b);
    const l = lignes[idx];
    let prix = l.prix_unitaire_jour;
    if (!l.prix_est_forfait && l.gamme_id && l.date_debut_prevue && l.date_fin_prevue) {
      const gamme = (gammesByCat[l.categorie_id] || []).find(g => g.gamme_id === l.gamme_id);
      if (gamme?.grille_tarifaire?.length) {
        const duree = calcDureeWorkingDays(l.date_debut_prevue, l.date_fin_prevue, newExclus);
        const autoPrix = getPrixJourV2(duree, gamme.grille_tarifaire);
        if (autoPrix != null) prix = String(autoPrix);
      }
    }
    updateLigne(idx, { jours_exclus: newExclus, prix_unitaire_jour: prix ?? '' });
  };

  const onTogglePrixForfait = (idx) => {
    const l = lignes[idx];
    const newForfait = !l.prix_est_forfait;
    if (!newForfait && l.gamme_id && l.date_debut_prevue && l.date_fin_prevue) {
      const gamme = (gammesByCat[l.categorie_id] || []).find(g => g.gamme_id === l.gamme_id);
      if (gamme?.grille_tarifaire?.length) {
        const duree = calcDureeWorkingDays(l.date_debut_prevue, l.date_fin_prevue, l.jours_exclus || [5, 6]);
        const autoPrix = getPrixJourV2(duree, gamme.grille_tarifaire);
        if (autoPrix != null) { updateLigne(idx, { prix_est_forfait: false, prix_unitaire_jour: String(autoPrix) }); return; }
      }
    }
    updateLigne(idx, { prix_est_forfait: newForfait });
  };

  const isFinancialLocked = editing && editing.statut !== 'brouillon' && editing.statut !== 'en_attente_signature';

  // Services formalisés modifiés localement (désignation / qté / PU / TVA) → avenant groupé
  const svcModifies = (() => {
    if (!isFinancialLocked) return [];
    const isTermine = editing?.statut === 'termine';
    return localServices.filter(s => {
      if (pendingServiceIds.has(s.service_id)) return false;
      // Pour les contrats terminés, autoriser la correction des services facturés
      if (!isTermine && ['facture', 'annule'].includes(s.statut_service)) return false;
      if (isTermine && s.statut_service === 'annule') return false;
      const orig = initialServices.find(o => o.service_id === s.service_id);
      if (!orig) return false;
      return (s.designation || '') !== (orig.designation || '')
        || Number(s.quantite ?? 1) !== Number(orig.quantite ?? 1)
        || Number(s.prix_unitaire_ht ?? 0) !== Number(orig.prix_unitaire_ht ?? 0)
        || Number(s.taux_tva ?? 20) !== Number(orig.taux_tva ?? 20);
    });
  })();

  // Nouveaux services ajoutés localement (pas encore persistés) → créés via l'avenant
  const newServicesLocal = localServices.filter(s => s.service_id?.startsWith('LOCALV2-'));

  const buildLignesPayload = () => lignes
    .filter(l => l.categorie_id && l.gamme_id && l.date_debut_prevue && l.date_fin_prevue)
    .map(l => ({
      categorie_id: l.categorie_id, categorie_nom: l.categorie_nom, gamme_id: l.gamme_id, gamme_nom: l.gamme_nom,
      quantite: Number(l.quantite) || 1, date_debut_prevue: l.date_debut_prevue, date_fin_prevue: l.date_fin_prevue,
      prix_unitaire_jour: l.prix_unitaire_jour !== '' && l.prix_unitaire_jour != null ? Number(l.prix_unitaire_jour) : null,
      jours_exclus: Array.isArray(l.jours_exclus) ? l.jours_exclus : [5, 6],
      note: l.note || null,
    }));

  const save = async () => {
    if (savingRef.current) return;  // double-clic : un enregistrement est déjà en cours
    if (!form.client.client_id) { toast.error('Sélectionnez un client'); return; }
    const lignesPayload = buildLignesPayload();
    if (!isFinancialLocked && !lignesPayload.length) { toast.error('Ajoutez au moins une ligne complète (catégorie, gamme, dates)'); return; }

    savingRef.current = true;
    setSaving(true);
    try {
      const header = {
        client_id: form.client.client_id, client_type: form.client.client_type, client_nom: form.client.client_nom,
        contact_id: form.contact_id || null, contact_nom: form.contact_nom || null,
        titre: form.titre || null, ref_client: form.ref_client || null,
        adresse_livraison: form.adresse_livraison || null,
        mode_reglement: form.mode_reglement || null,
        conditions_paiement_nom: form.conditions_paiement_nom || null,
        notes: form.notes || null,
        commercial_id: form.commercial_id || null,
      };
      if (!editing) {
        await api.createContratV2({ ...header, lignes: lignesPayload });
        toast.success('Contrat créé');
      } else {
        await api.updateContratV2(editing.contrat_id, header);
        if (!isFinancialLocked) {
          const initialIds = new Set(initialLignes.map(l => l.ligne_id));
          const currentIds = new Set(lignes.filter(l => l.ligne_id).map(l => l.ligne_id));
          for (const l of initialLignes) {
            if (!currentIds.has(l.ligne_id)) await api.deleteLigneV2(l.ligne_id);
          }
          for (const l of lignes) {
            if (!l.categorie_id || !l.gamme_id || !l.date_debut_prevue || !l.date_fin_prevue) continue;
            const payload = {
              categorie_id: l.categorie_id, categorie_nom: l.categorie_nom, gamme_id: l.gamme_id, gamme_nom: l.gamme_nom,
              quantite: Number(l.quantite) || 1, date_debut_prevue: l.date_debut_prevue, date_fin_prevue: l.date_fin_prevue,
              prix_unitaire_jour: l.prix_unitaire_jour !== '' && l.prix_unitaire_jour != null ? Number(l.prix_unitaire_jour) : null,
              jours_exclus: Array.isArray(l.jours_exclus) ? l.jours_exclus : [5, 6],
              note: l.note || null,
            };
            if (l.ligne_id && initialIds.has(l.ligne_id)) await api.updateLigneV2(l.ligne_id, payload);
            else if (!l.ligne_id) await api.addLigneContratV2(editing.contrat_id, payload);
          }
        }
        toast.success('Contrat mis à jour');
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement"); }
    finally { savingRef.current = false; setSaving(false); }
  };

  const transition = async (statut) => {
    if (!editing) return;
    try {
      const res = await api.transitionContratV2(editing.contrat_id, { statut });
      toast.success('Statut mis à jour');
      setEditing(res.data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Transition impossible'); }
  };

  // ── Récapitulatif : totaux pour l'onglet Matériels ──────────────────────────
  const recapLocationHT = lignes.reduce((sum, l) => {
    const joursExclus = Array.isArray(l.jours_exclus) ? l.jours_exclus : [5, 6];
    const duree = calcDureeWorkingDays(l.date_debut_prevue, l.date_fin_prevue, joursExclus);
    if (!l.prix_unitaire_jour || duree <= 0) return sum;
    return sum + Number(l.prix_unitaire_jour) * duree * (Number(l.quantite) || 1);
  }, 0);
  const recapLocationTVAtaux = Number(editing?.taux_tva_location) || 0;
  const recapLocationTVA    = recapLocationHT * recapLocationTVAtaux / 100;
  const recapServicesHT = localServices
    .filter(s => s.type_ligne === 'prestation' && s.statut_service !== 'annule')
    .reduce((sum, s) => {
      let m;
      if (s.montant_estime != null) {
        m = Number(s.montant_estime);
      } else if (s.mode_facturation === 'POURCENTAGE') {
        m = (Number(s.pourcentage_application) || 0) / 100 * recapLocationHT;
      } else {
        m = (Number(s.quantite) || 0) * (Number(s.prix_unitaire_ht) || 0);
      }
      return sum + m;
    }, 0);
  const recapServicesTVA = localServices
    .filter(s => s.type_ligne === 'prestation' && s.statut_service !== 'annule')
    .reduce((sum, s) => {
      let m;
      if (s.montant_estime != null) {
        m = Number(s.montant_estime);
      } else if (s.mode_facturation === 'POURCENTAGE') {
        m = (Number(s.pourcentage_application) || 0) / 100 * recapLocationHT;
      } else {
        m = (Number(s.quantite) || 0) * (Number(s.prix_unitaire_ht) || 0);
      }
      return sum + m * (Number(s.taux_tva) || 0) / 100;
    }, 0);

  return (
    <div className="space-y-6" data-testid="locations-v2-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Location V2</h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Nouveau module Contrats — réservation par catégorie (Phase 1)</p>
        </div>
        {!isReadOnly && (
          <button
            onClick={openCreate}
            data-testid="locations-v2-new-contrat-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm text-white"
            style={{ background: 'var(--primary-solid)' }}
          >
            <Plus size={16} /> Nouveau contrat
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="locations-v2-kpi-cards">
        <FilterDropdown big icon="📄" label="Contrats actifs" testid="kpi-contrats-actifs"
          value={(kpis?.contrat?.signe || 0) + (kpis?.contrat?.en_cours || 0)}
          options={[
            { key: 'signe', label: 'Signé / Réservé', count: kpis?.contrat?.signe ?? 0 },
            { key: 'en_cours', label: 'Actif / En cours', count: kpis?.contrat?.en_cours ?? 0 },
          ]}
          selected={filtreStatut} onToggle={toggleFiltre(setFiltreStatut)} />
        <FilterDropdown big icon="🚚" label="Livraisons en cours" testid="kpi-livraisons"
          value={(kpis?.livraisons?.a_livrer_sortie || 0) + (kpis?.livraisons?.a_livrer_retour || 0)}
          options={[
            { key: 'a_livrer_sortie', label: 'Sorties à livrer', count: kpis?.livraisons?.a_livrer_sortie ?? 0 },
            { key: 'a_livrer_retour', label: 'Retours à livrer', count: kpis?.livraisons?.a_livrer_retour ?? 0 },
          ]}
          selected={filtreLivraisons} onToggle={toggleFiltre(setFiltreLivraisons)} />
        <FilterDropdown big icon="⚠️" label="Alertes" testid="kpi-alertes"
          value={(kpis?.livraisons?.retard_sortie || 0) + (kpis?.livraisons?.retard_retour || 0) + (kpis?.livraisons?.retard_restitution || 0)}
          options={[
            { key: 'retard_sortie', label: 'Sorties en retard', count: kpis?.livraisons?.retard_sortie ?? 0 },
            { key: 'retard_retour', label: 'Retours en retard', count: kpis?.livraisons?.retard_retour ?? 0 },
            { key: 'retard_restitution', label: 'Restitutions en retard', count: kpis?.livraisons?.retard_restitution ?? 0 },
          ]}
          selected={filtreAlertes} onToggle={toggleFiltre(setFiltreAlertes)} />
        <FilterDropdown big icon="💳" label="CA en cours" testid="kpi-ca-en-cours"
          value={fmtEur(filtreFacturation.length > 0
            ? filtreFacturation.reduce((s, k) => s + (kpis?.montant_encours_par_statut?.[k] || 0), 0)
            : (kpis?.montant_encours_ht || 0))}
          options={[
            { key: 'encours_a_facturer', label: 'Encours à facturer', count: kpis?.facturation?.encours_a_facturer ?? 0 },
            { key: 'restitue_a_solder', label: 'Restitué à solder', count: kpis?.facturation?.restitue_a_solder ?? 0 },
          ]}
          selected={filtreFacturation} onToggle={toggleFiltre(setFiltreFacturation)} />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl border flex-1 min-w-56 max-w-md" style={{ borderColor: 'var(--border)', background: 'var(--card)' }}>
          <Search size={14} style={{ color: 'var(--text-muted)' }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher un numéro ou un client…"
            className="flex-1 text-sm outline-none bg-transparent"
            data-testid="locations-v2-search-input"
          />
        </div>
        <FilterDropdown icon="📄" label="Statut contrat" testid="filtre-statut-contrat"
          options={['brouillon', 'en_attente_signature', 'termine', 'annule'].map(k => ({ key: k, label: STATUTS[k].label, count: kpis?.contrat?.[k] ?? 0 }))}
          selected={filtreStatut} onToggle={toggleFiltre(setFiltreStatut)} />
        <FilterDropdown icon="🚚" label="Logistique" testid="filtre-logistique"
          options={['non_livre', 'sur_site', 'partiellement_restitue', 'restitue'].map(k => ({ key: k, label: STATUTS_LOGISTIQUE[k].label, count: kpis?.logistique?.[k] ?? 0 }))}
          selected={filtreLogistique} onToggle={toggleFiltre(setFiltreLogistique)} />
        <FilterDropdown icon="💳" label="Facturation" testid="filtre-facturation"
          options={['non_facturable', 'brouillon', 'facture_a_jour'].map(k => ({ key: k, label: STATUTS_FACTURATION[k].label, count: kpis?.facturation?.[k] ?? 0 }))}
          selected={filtreFacturation} onToggle={toggleFiltre(setFiltreFacturation)} />
      </div>

      <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)', background: 'var(--card)' }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: 'var(--bg-subtle)' }}>
              <th className="text-left px-4 py-2.5 font-semibold" style={{ color: 'var(--text-secondary)' }}>Numéro</th>
              <th className="text-left px-4 py-2.5 font-semibold" style={{ color: 'var(--text-secondary)' }}>Client</th>
              <th className="text-left px-4 py-2.5 font-semibold" style={{ color: 'var(--text-secondary)' }}>Suivi par</th>
              <th className="text-left px-4 py-2.5 font-semibold" style={{ color: 'var(--text-secondary)' }}>📄 Statut contrat</th>
              <th className="text-left px-4 py-2.5 font-semibold" style={{ color: 'var(--text-secondary)' }}>🚚 Logistique</th>
              <th className="text-left px-4 py-2.5 font-semibold" style={{ color: 'var(--text-secondary)' }}>💳 Facturation</th>
              <th className="text-left px-4 py-2.5 font-semibold" style={{ color: 'var(--text-secondary)' }}>Début</th>
              <th className="text-left px-4 py-2.5 font-semibold" style={{ color: 'var(--text-secondary)' }}>Fin</th>
              <th className="text-right px-4 py-2.5 font-semibold" style={{ color: 'var(--text-secondary)' }}>Montant total HT</th>
            </tr>
          </thead>
          <tbody>
            {!loading && contrats.length === 0 && (
              <tr><td colSpan={9} className="text-center py-8" style={{ color: 'var(--text-muted)' }}>Aucun contrat Location V2 pour le moment</td></tr>
            )}
            {contrats.map(c => (
              <tr key={c.contrat_id} onClick={() => openEdit(c)} data-testid={`locations-v2-contrat-row-${c.contrat_id}`} className="border-t hover:bg-gray-50 cursor-pointer" style={{ borderColor: 'var(--border-light)' }}>
                <td className="px-4 py-2.5 font-medium" style={{ color: 'var(--primary-solid)' }} data-testid={`locations-v2-open-btn-${c.contrat_id}`}>{c.numero}</td>
                <td className="px-4 py-2.5" style={{ color: 'var(--text)' }}>{c.client_nom || '—'}</td>
                <td className="px-4 py-2.5" style={{ color: 'var(--text-muted)' }} data-testid={`locations-v2-commercial-${c.contrat_id}`}>{c.commercial_nom || '—'}</td>
                <td className="px-4 py-2.5"><StatutBadge statut={c.statut} /></td>
                <td className="px-4 py-2.5"><LogistiqueBadge statut={c.statut_logistique} /></td>
                <td className="px-4 py-2.5"><FacturationBadge statut={c.statut_facturation} /></td>
                <td className="px-4 py-2.5" style={{ color: 'var(--text-muted)' }}>{fmtDate(c.date_debut_initiale)}</td>
                <td className="px-4 py-2.5" style={{ color: 'var(--text-muted)' }}>{fmtDate(c.date_fin_actuelle)}</td>
                <td className="px-4 py-2.5 text-right" data-testid={`locations-v2-montant-${c.contrat_id}`}>
                  <div className="flex flex-col items-end gap-0.5">
                    <span className="font-semibold" style={{ color: 'var(--text)' }}>{fmtEur(c.montant_total_ht || 0)}</span>
                    {c.nb_avenants > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded border"
                        style={{ color: '#B45309', background: '#FFFBEB', borderColor: '#FDE68A' }}
                        title="Montant consolidé incluant les avenants signés">
                        <FileSignature size={11} />
                        {c.nb_avenants} avenant{c.nb_avenants > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SidePanel
        open={panelOpen}
        onOpenChange={setPanelOpen}
        title={editing ? editing.numero : 'Nouveau contrat Location V2'}
        copyValue={editing ? editing.numero : undefined}
        subtitle={editing ? <StatutBadge statut={editing.statut} /> : null}
        size="lg"
        footer={(
        <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2 flex-wrap">

              {/* ── Brouillon → Bouton "Envoyer le devis" ─────────────── */}
              {editing && editing.statut === 'brouillon' && (
                <>
                  <div className="relative" ref={envoyerDevisRef}>
                    <button
                      data-testid="btn-envoyer-devis-v2"
                      onClick={() => setEnvoyerDevisOpen(o => !o)}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
                      style={{ background: '#1D4ED8' }}>
                      <Send size={13} />
                      Envoyer le devis
                      <ChevronDown size={13} style={{ marginLeft: 2 }} />
                    </button>
                    {envoyerDevisOpen && (
                      <div className="absolute bottom-full mb-1.5 left-0 rounded-xl border bg-white shadow-xl z-50 overflow-hidden"
                        style={{ borderColor: 'var(--border)', minWidth: 210 }}>
                        <button
                          data-testid="btn-envoyer-devis-email-v2"
                          onClick={() => openMailDevisModalV2(null)}
                          className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors border-b"
                          style={{ borderColor: '#F3F4F6' }}>
                          <Mail size={14} style={{ color: '#1D4ED8' }} />
                          <div>
                            <div className="text-sm font-medium" style={{ color: '#111827' }}>Envoyer par email</div>
                            <div className="text-xs" style={{ color: '#6B7280' }}>PDF en pièce jointe</div>
                          </div>
                        </button>
                        <button
                          data-testid="btn-imprimer-devis-v2"
                          onClick={handleImprimerDevisV2}
                          className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                          <Printer size={14} style={{ color: '#6B7280' }} />
                          <div>
                            <div className="text-sm font-medium" style={{ color: '#111827' }}>Imprimer / PDF</div>
                            <div className="text-xs" style={{ color: '#6B7280' }}>Télécharge le PDF</div>
                          </div>
                        </button>
                      </div>
                    )}
                  </div>
                  {editing.validation?.devis_email_envoye && (
                    <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                      style={{ background: '#EFF6FF', color: '#1D4ED8' }}>
                      <Mail size={11} />
                      Envoyé à {editing.validation.devis_email_destinataire || '—'}
                    </span>
                  )}
                </>
              )}

              {/* ── Brouillon, en cours, retour, signe, termine → Dropdown signature ─── */}
              {editing && (
                editing.statut === 'brouillon'
                || editing.statut === 'signe'
                || (editing.statut === 'en_attente_signature' && !editing.validation?.signature_web_token)
                || (['en_cours', 'retour', 'termine'].includes(editing.statut) && sigMenuOpen)
              ) && (
                <div className="relative" ref={sigMenuRef}>
                  <button onClick={() => setSigMenuOpen(o => !o)} data-testid="locations-v2-sig-menu-btn"
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold"
                    style={{ background: '#D97706', color: '#fff' }}>
                    <FileSignature size={13} /> Envoyer en signature
                    <ChevronDown size={12} style={{ transform: sigMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                  </button>
                  {sigMenuOpen && (
                    <div className="absolute bottom-full mb-1 left-0 w-56 rounded-xl border shadow-xl z-50 overflow-hidden"
                      style={{ background: '#fff', borderColor: 'var(--border)' }}>
                      <button onClick={() => openSigMode('tablette')} data-testid="sig-mode-tablette"
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs text-left hover:bg-indigo-50 transition-colors">
                        <Tablet size={14} style={{ color: '#6366F1' }} />
                        <div><p className="font-semibold" style={{ color: 'var(--text)' }}>Tablette terrain</p><p style={{ color: 'var(--text-muted)' }}>Signature sur tablette</p></div>
                      </button>
                      <button onClick={() => openSigMode('ordinateur')} data-testid="sig-mode-ordinateur"
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs text-left hover:bg-indigo-50 transition-colors">
                        <Monitor size={14} style={{ color: '#6366F1' }} />
                        <div><p className="font-semibold" style={{ color: 'var(--text)' }}>Ordinateur</p><p style={{ color: 'var(--text-muted)' }}>Signature à la souris</p></div>
                      </button>
                      <button onClick={() => openSigMode('mail')} data-testid="sig-mode-mail"
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs text-left hover:bg-indigo-50 transition-colors">
                        <Mail size={14} style={{ color: '#6366F1' }} />
                        <div><p className="font-semibold" style={{ color: 'var(--text)' }}>Envoi PDF par mail</p><p style={{ color: 'var(--text-muted)' }}>Le client signe en retour</p></div>
                      </button>
                      <button onClick={() => openSigMode('lien')} data-testid="sig-mode-lien"
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs text-left hover:bg-indigo-50 transition-colors">
                        <LinkIcon size={14} style={{ color: '#6366F1' }} />
                        <div><p className="font-semibold" style={{ color: 'var(--text)' }}>Lien en ligne</p><p style={{ color: 'var(--text-muted)' }}>Le client signe sur une page web</p></div>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* ── En attente : badge lien + actions ─────────────────────── */}
              {editing && editing.statut === 'en_attente_signature' && editing.validation?.signature_web_token && !editing.validation?.signature_recue && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: '#FEF3C7', color: '#92400E' }}>
                    Lien envoyé à {editing.validation.signataire_nom || '…'}
                  </span>
                  <button onClick={copierLienSignature} data-testid="sig-lien-copier"
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold"
                    style={{ background: '#475569', color: '#fff' }}>
                    <Copy size={12} /> Copier
                  </button>
                  <button onClick={() => openSigMode('relien')} data-testid="sig-lien-renvoyer"
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold"
                    style={{ background: '#4338CA', color: '#fff' }}>
                    <Send size={12} /> Renvoyer
                  </button>
                  <button onClick={handleAnnulerLien} data-testid="sig-lien-annuler"
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold"
                    style={{ background: '#DC2626', color: '#fff' }}>
                    <XIcon size={12} /> Révoquer
                  </button>
                </div>
              )}

              {/* ── En attente : confirmation signature reçue ─────────────── */}
              {editing && editing.statut === 'en_attente_signature' && !editing.validation?.signature_web_token && (
                <>
                  <button onClick={() => openSigMode('recue')} data-testid="locations-v2-sig-recue-btn"
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold" style={{ background: '#16A34A', color: '#fff' }}>
                    <CheckCircle2 size={13} /> Signature reçue
                  </button>
                  <button onClick={() => transition('brouillon')} data-testid="locations-v2-transition-brouillon-btn"
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold" style={{ background: '#6B7280', color: '#fff' }}>
                    <RotateCcw size={13} /> Repasser en brouillon
                  </button>
                </>
              )}

              {editing && !isFinancialLocked && (
                <button onClick={() => transition('annule')} data-testid="locations-v2-transition-annule-btn"
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold" style={{ background: '#DC2626', color: '#fff' }}>
                  <Ban size={13} /> Annuler
                </button>
              )}
              {/* Annulation : avant livraison pour tous, après livraison pour admin uniquement (si pas de facture) */}
              {editing && isFinancialLocked && !['annule'].includes(editing.statut) && (
                (['en_cours', 'retour', 'termine'].includes(editing.statut)
                  ? (user?.role === 'super_admin' || user?.role === 'admin_org')
                  : true
                )
              ) && (
                <button onClick={handleAnnulerContrat}
                  data-testid="locations-v2-annuler-avant-livraison-btn"
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold" style={{ background: '#DC2626', color: '#fff' }}>
                  <Ban size={13} /> Annuler le contrat
                </button>
              )}
              {/* Remettre en brouillon un contrat annulé */}
              {editing?.statut === 'annule' && (
                <button onClick={handleRemettreEnBrouillon}
                  data-testid="locations-v2-remettre-en-brouillon-btn"
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold" style={{ background: '#6B7280', color: '#fff' }}>
                  <RotateCcw size={13} /> Remettre en brouillon
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isFinancialLocked && (hasLigneChanges || pendingServiceIds.size > 0 || svcModifies.length > 0 || newServicesLocal.length > 0) && (<>
                <button type="button" onClick={handleValiderSansAvenant} disabled={savingSession}
                  data-testid="btn-valider-sans-avenant"
                  className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-xl font-semibold text-white"
                  style={{ background: savingSession ? '#9CA3AF' : '#7C3AED', cursor: savingSession ? 'not-allowed' : 'pointer' }}>
                  {savingSession ? '…' : 'Valider sans avenant'}
                </button>
                <button type="button" onClick={handleValiderSession} disabled={savingSession}
                  data-testid="btn-valider-par-avenant"
                  className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-xl font-semibold text-white"
                  style={{ background: savingSession ? '#9CA3AF' : '#D97706', cursor: savingSession ? 'not-allowed' : 'pointer' }}>
                  {savingSession ? 'Enregistrement…' : 'Valider avec avenant'}
                </button>
              </>)}
              <button onClick={save} disabled={saving} data-testid="locations-v2-save-btn"
                className="px-5 py-2 rounded-xl font-semibold text-sm text-white" style={{ background: 'var(--primary-solid)', cursor: saving ? 'not-allowed' : 'pointer' }}>
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        )}
      >
        <div className="p-6 space-y-6">
          {/* ── Bannière contrat annulé ──────────────────────────── */}
          {editing?.statut === 'annule' && (
            <div className="px-4 py-3 rounded-xl flex items-start gap-3"
              style={{ background: '#FEF2F2', border: '2px solid #FECACA' }}>
              <Ban size={18} style={{ color: '#DC2626', flexShrink: 0, marginTop: 1 }} />
              <div className="min-w-0">
                <div className="font-bold text-sm" style={{ color: '#991B1B' }}>Contrat Annulé</div>
                {editing.motif_annulation && (
                  <div className="text-xs mt-0.5" style={{ color: '#B91C1C' }}>
                    Motif : {editing.motif_annulation}
                  </div>
                )}
                {editing.commentaire_annulation && (
                  <div className="text-xs mt-0.5 italic" style={{ color: '#DC2626' }}>
                    {editing.commentaire_annulation}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="flex gap-1 p-1 rounded-lg" style={{ background: 'var(--bg-subtle)' }}>
            <button onClick={() => setActiveTab('info')} data-testid="locations-v2-tab-info"
              className="flex-1 text-xs font-semibold py-1.5 rounded-md transition-colors"
              style={{ background: activeTab === 'info' ? 'var(--card)' : 'transparent', color: activeTab === 'info' ? 'var(--text)' : 'var(--text-muted)' }}>
              Informations
            </button>
            <button onClick={() => setActiveTab('materiels')} data-testid="locations-v2-tab-materiels"
              className="flex-1 text-xs font-semibold py-1.5 rounded-md transition-colors"
              style={{ background: activeTab === 'materiels' ? 'var(--card)' : 'transparent', color: activeTab === 'materiels' ? 'var(--text)' : 'var(--text-muted)' }}>
              Matériels
            </button>
            {editing && (
              <>
                <button onClick={() => setActiveTab('terrain')} data-testid="locations-v2-tab-terrain"
                  className="flex-1 text-xs font-semibold py-1.5 rounded-md transition-colors"
                  style={{ background: activeTab === 'terrain' ? 'var(--card)' : 'transparent', color: activeTab === 'terrain' ? 'var(--text)' : 'var(--text-muted)' }}>
                  Logistique
                </button>
                <button onClick={() => setActiveTab('avenants')} data-testid="locations-v2-tab-contrats"
                  className="flex-1 text-xs font-semibold py-1.5 rounded-md transition-colors"
                  style={{ background: activeTab === 'avenants' ? 'var(--card)' : 'transparent', color: activeTab === 'avenants' ? 'var(--text)' : 'var(--text-muted)' }}>
                  Contrats
                </button>
                <button onClick={() => setActiveTab('suspensions')} data-testid="locations-v2-tab-suspensions"
                  className="flex-1 text-xs font-semibold py-1.5 rounded-md transition-colors"
                  style={{ background: activeTab === 'suspensions' ? 'var(--card)' : 'transparent', color: activeTab === 'suspensions' ? 'var(--text)' : 'var(--text-muted)' }}>
                  Suspensions
                </button>
                <button onClick={() => setActiveTab('documents')} data-testid="locations-v2-tab-documents"
                  className="flex-1 text-xs font-semibold py-1.5 rounded-md transition-colors"
                  style={{ background: activeTab === 'documents' ? 'var(--card)' : 'transparent', color: activeTab === 'documents' ? 'var(--text)' : 'var(--text-muted)' }}>
                  Documents{docCount > 0 ? ` (${docCount})` : ''}
                </button>
                <button onClick={() => setActiveTab('facturation')} data-testid="locations-v2-tab-facturation"
                  className="flex-1 text-xs font-semibold py-1.5 rounded-md transition-colors"
                  style={{ background: activeTab === 'facturation' ? 'var(--card)' : 'transparent', color: activeTab === 'facturation' ? 'var(--text)' : 'var(--text-muted)' }}>
                  Facturation
                </button>
                <button onClick={() => setActiveTab('activites')} data-testid="locations-v2-tab-activites"
                  className="flex-1 text-xs font-semibold py-1.5 rounded-md transition-colors flex items-center justify-center gap-1.5"
                  style={{ background: activeTab === 'activites' ? 'var(--card)' : 'transparent', color: activeTab === 'activites' ? 'var(--text)' : 'var(--text-muted)' }}>
                  Activités
                  {hasActivity && <ActivityPulse />}
                </button>
              </>
            )}
          </div>

          {activeTab === 'info' && (
          <>
          <div>
            <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Client</label>
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <ClientSearchField
                  value={form.client}
                  onChange={(v) => setForm(f => ({ ...f, client: v, contact_id: null, contact_nom: null }))}
                  readOnly={false}
                  testId="locations-v2-client-search-input"
                />
              </div>
              {form.mode_reglement && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg font-semibold flex-shrink-0"
                  style={form.mode_reglement === 'comptant'
                    ? { background: '#F0FDF4', color: '#15803D', border: '1px solid #86EFAC' }
                    : { background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #93C5FD' }}>
                  <CreditCard size={10} />
                  {form.mode_reglement === 'comptant' ? 'Comptant' : 'En compte'}
                  {form.conditions_paiement_nom && <span className="font-normal opacity-70 ml-1">— {form.conditions_paiement_nom}</span>}
                </span>
              )}
            </div>
          </div>

          {/* Interlocuteur (entreprises) + Suivi par — tableau 2 colonnes */}
          {(() => { const showContact = form.client?.client_id && form.client?.client_type === 'entreprise'; return (
          <div className="grid gap-3" style={{ gridTemplateColumns: showContact ? '1fr 1fr' : '1fr' }}>
          {showContact && (
            <div>
              <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Interlocuteur</label>
              <div className="relative" ref={contactDropRef} data-testid="v2-contact-select-wrapper">
                <button
                  type="button"
                  data-testid="v2-contact-select"
                  onClick={() => { if (!contactsLoading) setContactDropOpen(o => !o); }}
                  disabled={contactsLoading}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg border text-sm text-left min-h-[38px]"
                  style={{
                    borderColor: contactDropOpen ? 'var(--primary-solid)' : 'var(--border)',
                    background: 'var(--bg)',
                    cursor: contactsLoading ? 'default' : 'pointer',
                    opacity: contactsLoading ? 0.6 : 1,
                  }}>
                  <span style={{ color: form.contact_nom ? 'var(--text)' : 'var(--text-muted)' }}>
                    {contactsLoading
                      ? <span className="flex items-center gap-1.5"><span className="w-3 h-3 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin inline-block" />Chargement…</span>
                      : form.contact_nom || (contacts.length === 0 ? '— Aucun contact disponible —' : '— Sélectionnez un interlocuteur —')}
                  </span>
                  {<ChevronDown size={14} style={{ flexShrink: 0, color: 'var(--text-muted)', transform: contactDropOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />}
                </button>
                {contactDropOpen && contacts.length > 0 && (
                  <div className="absolute left-0 top-full mt-1 w-full rounded-xl border shadow-lg z-50 overflow-auto"
                    style={{ borderColor: 'var(--border)', background: '#fff', maxHeight: 220 }}>
                    {/* Option vide pour désélectionner */}
                    <button type="button" onClick={() => { setForm(f => ({ ...f, contact_id: null, contact_nom: null })); setContactDropOpen(false); }}
                      className="w-full flex items-center px-3 py-2 text-sm text-left hover:bg-slate-50 transition-colors"
                      style={{ color: 'var(--text-muted)' }}>
                      — Aucun interlocuteur —
                    </button>
                    {contacts.map(c => {
                      const id = c.personne_id || c.contact_id;
                      const nom = `${c.prenom || ''} ${c.nom || ''}`.trim() || c.nom_complet || String(id);
                      const isSelected = form.contact_id === id;
                      return (
                        <button key={id} type="button" data-testid={`v2-contact-option-${id}`}
                          onClick={() => { setForm(f => ({ ...f, contact_id: id, contact_nom: nom })); setContactDropOpen(false); }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-indigo-50 transition-colors"
                          style={{ background: isSelected ? '#EEF2FF' : 'transparent' }}>
                          <span className="flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center"
                            style={{ borderColor: isSelected ? '#6366F1' : 'var(--border)', background: isSelected ? '#6366F1' : 'transparent' }}>
                            {isSelected && <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                          </span>
                          <span className="flex-1 truncate" style={{ color: 'var(--text)' }}>{nom}</span>
                          {c.poste && <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-muted)' }}>{c.poste}</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Suivi par — commercial (table users), défaut = créateur du contrat */}
          <div>
            <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Suivi par</label>
            <select
              data-testid="v2-suivi-par-select"
              value={form.commercial_id || ''}
              onChange={(e) => {
                const uid = e.target.value || null;
                const u = orgUsers.find(x => x.user_id === uid);
                setForm(f => ({ ...f, commercial_id: uid, commercial_nom: u?.name || null }));
              }}
              className="w-full px-3 py-2 rounded-lg border text-sm min-h-[38px]"
              style={{ borderColor: 'var(--border)', background: 'var(--bg)', color: form.commercial_id ? 'var(--text)' : 'var(--text-muted)' }}>
              <option value="">{form.commercial_nom ? `${form.commercial_nom} (hors liste)` : '— Sélectionnez —'}</option>
              {orgUsers.map(u => (
                <option key={u.user_id} value={u.user_id} data-testid={`v2-suivi-par-option-${u.user_id}`}>{u.name || u.email}</option>
              ))}
            </select>
          </div>
          </div>
          ); })()}

          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Titre</label>
              <input value={form.titre} onChange={e => setForm(f => ({ ...f, titre: e.target.value }))}
                data-testid="locations-v2-titre-input" className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border)' }} />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Réf. client</label>
              <input value={form.ref_client} onChange={e => setForm(f => ({ ...f, ref_client: e.target.value }))}
                disabled={(editing?.nb_factures || 0) > 0}
                data-testid="locations-v2-ref-client-input" className="w-full px-3 py-2 rounded-lg border text-sm"
                style={{ borderColor: 'var(--border)', ...((editing?.nb_factures || 0) > 0 ? { background: 'var(--bg-subtle)', color: 'var(--text-muted)', cursor: 'not-allowed', opacity: 0.7 } : {}) }} />
              {(editing?.nb_factures || 0) > 0 && (
                <div className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }} data-testid="locations-v2-ref-client-locked-hint">
                  Référence utilisée pour la première facture — figée dès qu'une facture existe. Pour une autre référence (PO / BdC), utilisez l'onglet Facturation.
                </div>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Adresse de livraison</label>
              <textarea value={form.adresse_livraison} onChange={e => { setForm(f => ({ ...f, adresse_livraison: e.target.value })); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} rows={2}
                data-testid="locations-v2-adresse-livraison-input" placeholder="Adresse, ville, code postal…" className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border)', resize: 'none', overflow: 'hidden', minHeight: '60px' }} />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Notes</label>
            <textarea ref={notesRefCallback} value={form.notes} onChange={e => { setForm(f => ({ ...f, notes: e.target.value })); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} rows={4}
              data-testid="locations-v2-notes-input" className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border)', resize: 'none', overflow: 'hidden', minHeight: '96px' }} />
          </div>

          </>
          )}

          {activeTab === 'materiels' && (
          <>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Lignes de réservation (par catégorie)</label>
              <button onClick={addLigneRow} data-testid="locations-v2-add-ligne-btn"
                className="flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg" style={{ color: 'var(--primary-solid)' }}>
                <Plus size={13} /> Ajouter une ligne
              </button>
            </div>
            <div className="space-y-2">
              {lignes.map((l, idx) => {
                const joursExclus = l.jours_exclus || [5, 6];
                const duree = calcDureeWorkingDays(l.date_debut_prevue, l.date_fin_prevue, joursExclus);
                const estim = l.prix_unitaire_jour && duree > 0
                  ? Number(l.prix_unitaire_jour) * duree * (Number(l.quantite) || 1) : null;
                return (
                  <div key={idx} className="p-3 rounded-lg border space-y-2" style={{ borderColor: 'var(--border)' }}>
                    <div className="grid grid-cols-6 gap-2">
                    <select value={l.categorie_id} onChange={e => onCategorieChange(idx, e.target.value)} disabled={false}
                      data-testid={`locations-v2-ligne-categorie-${idx}`} className="col-span-2 px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }}>
                      <option value="">Catégorie…</option>
                      {categories.map(c => <option key={c.categorie_id} value={c.categorie_id}>{c.nom}</option>)}
                    </select>
                    <select value={l.gamme_id} onChange={e => onGammeChange(idx, e.target.value)} disabled={!l.categorie_id}
                      data-testid={`locations-v2-ligne-gamme-${idx}`} className="col-span-2 px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }}>
                      <option value="">Gamme…</option>
                      {(gammesByCat[l.categorie_id] || []).map(g => <option key={g.gamme_id} value={g.gamme_id}>{g.nom}</option>)}
                    </select>
                    <input type="number" min={1} value={l.quantite} onChange={e => updateLigne(idx, { quantite: e.target.value })} disabled={false}
                      data-testid={`locations-v2-ligne-quantite-${idx}`} placeholder="Qté" className="col-span-1 px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }} />
                    <button onClick={() => removeLigneRow(idx)} data-testid={`locations-v2-ligne-remove-${idx}`} className="col-span-1 flex items-center justify-center rounded-md hover:bg-red-50">
                      <Trash2 size={14} style={{ color: '#DC2626' }} />
                    </button>

                    <input type="date" value={l.date_debut_prevue} onChange={e => onDateChange(idx, 'date_debut_prevue', e.target.value)} disabled={false}
                      data-testid={`locations-v2-ligne-date-debut-${idx}`} className="col-span-2 px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }} />
                    <input type="date" value={l.date_fin_prevue} onChange={e => onDateChange(idx, 'date_fin_prevue', e.target.value)} disabled={false}
                      data-testid={`locations-v2-ligne-date-fin-${idx}`} className="col-span-2 px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }} />
                    <div className="col-span-2 flex items-center gap-1">
                      <input type="number" min={0} step="0.01" value={l.prix_unitaire_jour} onChange={e => updateLigne(idx, { prix_unitaire_jour: e.target.value, prix_est_forfait: e.target.value !== '' })} disabled={false}
                        data-testid={`locations-v2-ligne-prix-${idx}`} placeholder="Prix/j" className="flex-1 px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }} />
                      <button type="button" onClick={() => onTogglePrixForfait(idx)} disabled={false}
                        data-testid={`locations-v2-ligne-prix-toggle-${idx}`}
                        title={l.prix_est_forfait ? "Repasser en calcul automatique depuis la grille" : "Verrouiller ce prix comme forfait"}
                        className="flex-shrink-0 text-[10px] px-1.5 py-1 rounded font-semibold transition-colors"
                        style={{ background: l.prix_est_forfait ? '#FEF3C7' : '#DBEAFE', color: l.prix_est_forfait ? '#B45309' : '#1E40AF' }}>
                        {l.prix_est_forfait ? 'Forfait' : 'Auto'}
                      </button>
                    </div>
                    </div>

                    {/* ── Grille des jours facturés (L M M J V S D) ─────── */}
                    <div className="flex items-center gap-1.5 flex-wrap" data-testid={`locations-v2-ligne-jours-${idx}`}>
                      <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Jours facturés :</span>
                      {JOURS_SEMAINE.map((label, dayIdx) => {
                        const isExclu = joursExclus.includes(dayIdx);
                        return (
                          <button
                            key={dayIdx}
                            type="button"
                            disabled={false}
                            onClick={() => onJoursExclusToggle(idx, dayIdx)}
                            data-testid={`ligne-jour-${idx}-${dayIdx}`}
                            title={isExclu ? `Inclure ${['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi','Dimanche'][dayIdx]}` : `Exclure ${['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi','Dimanche'][dayIdx]}`}
                            className="w-6 h-6 text-[11px] font-bold rounded transition-colors flex items-center justify-center"
                            style={{
                              background: isExclu ? '#F3F4F6' : 'var(--primary-solid)',
                              color: isExclu ? '#9CA3AF' : '#fff',
                              border: `1px solid ${isExclu ? '#E5E7EB' : 'var(--primary-solid)'}`,
                              cursor: isFinancialLocked ? 'default' : 'pointer',
                            }}
                          >
                            {label}
                          </button>
                        );
                      })}
                      {duree > 0 && (
                        <span className="text-[10px] ml-1" style={{ color: 'var(--text-muted)' }}>
                          → <strong style={{ color: 'var(--text)' }}>{duree}</strong> j facturés
                        </span>
                      )}
                      {estim != null && (
                        <span className="text-[10px] ml-auto font-semibold" style={{ color: 'var(--text)' }}>
                          ≈ {fmtEur(estim)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {editing && (
            <div>
              {/* ── Titre section Services additionnels ── */}
              <div className="flex items-center justify-between mb-2 mt-4">
                <label className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Services additionnels</label>
              </div>

              {retraitServiceForm && (
                <div className="p-3 rounded-lg space-y-2 mb-2" style={{ background: '#FEF2F2', border: '1px solid #FCA5A5' }}>
                  <div className="text-xs font-semibold" style={{ color: '#991B1B' }}>Retirer le service (via avenant)</div>
                  <input type="date" value={retraitServiceForm.date_effet}
                    onChange={e => setRetraitServiceForm(f => ({ ...f, date_effet: e.target.value }))}
                    data-testid="retrait-service-date-effet-input" className="w-full px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }} />
                  <input placeholder="Motif" value={retraitServiceForm.motif}
                    onChange={e => setRetraitServiceForm(f => ({ ...f, motif: e.target.value }))}
                    data-testid="retrait-service-motif-input" className="w-full px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }} />
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setRetraitServiceForm(null)} className="text-xs px-3 py-1.5 rounded-lg" style={{ color: 'var(--text-muted)' }}>Annuler</button>
                    <button onClick={submitRetraitService} disabled={savingRetraitService}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white" style={{ background: '#DC2626' }}>
                      Créer l'avenant de retrait
                    </button>
                  </div>
                </div>
              )}

              {/* ── Tableau des services — même largeur que la section Lignes de réservation ── */}
              <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
                <div className="overflow-x-auto">
                <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-subtle)', borderBottom: '2px solid var(--border)' }}>
                      <th className="px-3 py-2 text-left text-xs font-semibold w-28" style={{ color: 'var(--text-secondary)' }}>Actions</th>
                      <th className="px-2 py-2 text-left text-xs font-semibold w-20" style={{ color: 'var(--text-secondary)' }}>Réf</th>
                      <th className="px-2 py-2 text-left text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Désignation</th>
                      <th className="px-2 py-2 text-right text-xs font-semibold w-16" style={{ color: 'var(--text-secondary)' }}>Qté</th>
                      <th className="px-2 py-2 text-right text-xs font-semibold w-24" style={{ color: 'var(--text-secondary)' }}>PU Net</th>
                      <th className="px-2 py-2 text-right text-xs font-semibold w-20" style={{ color: 'var(--text-secondary)' }}>TVA %</th>
                      <th className="px-2 py-2 text-right text-xs font-semibold w-24" style={{ color: 'var(--text-secondary)' }}>Total HT</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {localServices.map(s => (
                      <LigneSvcRowV2 key={s.service_id} s={s} isFinancialLocked={isFinancialLocked && !pendingServiceIds.has(s.service_id) && s.statut_service === 'facture'}
                        recapLocationHT={recapLocationHT}
                        onPatch={(field, value) => patchServiceField(s.service_id, field, value)}
                        onDelete={() => deleteService(s.service_id)}
                        onRetraitService={() => setRetraitServiceForm({ service_id: s.service_id, date_effet: '', motif: '' })}
                      />
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid var(--border)' }}>
                      <td colSpan={9} className="px-0 py-0">
                        <SvcAddRowV2 onSelect={sel => {
                          const payload = {
                            article_id: sel.article_id || null,
                            designation: sel.designation || '',
                            type_ligne: sel.type_ligne || 'prestation',
                            quantite: Number(sel.quantite) || 1,
                            prix_unitaire_ht: Number(sel.prix_unitaire_ht) || 0,
                            taux_tva: Number(sel.taux_tva) ?? 20,
                            mode_facturation: 'FORFAIT_UNIQUE',
                          };
                          if (isFinancialLocked) {
                            // Contrat signé : ligne LOCALE uniquement — rien n'est persisté
                            // tant que « Valider par un avenant » n'a pas été cliqué.
                            setLocalServices(prev => [...prev, { ...payload, service_id: `LOCALV2-${Date.now()}`, statut_service: 'actif' }]);
                            return;
                          }
                          api.createServiceV2(editing.contrat_id, payload)
                            .then(res => {
                              toast.success('Service ajouté');
                              const added = Array.isArray(res.data) ? res.data[res.data.length - 1] : null;
                              if (added?.service_id) setPendingServiceIds(prev => new Set([...prev, added.service_id]));
                              return refreshEditing();
                            })
                            .catch(e => toast.error(e?.response?.data?.detail || 'Erreur'));
                        }} />
                      </td>
                    </tr>
                  </tfoot>
                </table>
                </div>
              </div>
            </div>
          )}

          {/* ── Section Récapitulatif ── */}
          {(recapLocationHT > 0 || recapServicesHT > 0) && (
            <div>
            <div className="flex items-center justify-between mb-2 mt-4">
              <label className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Récapitulatif</label>
            </div>
            <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-subtle)' }}>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Récapitulatif</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Montant HT</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Montant TVA</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Montant TTC</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recapLocationHT > 0 && (
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        <td className="px-4 py-2" style={{ color: 'var(--text-muted)' }}>Location</td>
                        <td className="px-4 py-2 text-right font-medium" style={{ color: 'var(--text)' }}>{fmtEur(recapLocationHT)}</td>
                        <td className="px-4 py-2 text-right font-medium" style={{ color: 'var(--text)' }}>{fmtEur(recapLocationTVA)}</td>
                        <td className="px-4 py-2 text-right font-medium" style={{ color: 'var(--text)' }}>{fmtEur(recapLocationHT + recapLocationTVA)}</td>
                      </tr>
                    )}
                    {recapServicesHT > 0 && (
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        <td className="px-4 py-2" style={{ color: 'var(--text-muted)' }}>Services additionnels</td>
                        <td className="px-4 py-2 text-right font-medium" style={{ color: 'var(--text)' }}>{fmtEur(recapServicesHT)}</td>
                        <td className="px-4 py-2 text-right font-medium" style={{ color: 'var(--text)' }}>{fmtEur(recapServicesTVA)}</td>
                        <td className="px-4 py-2 text-right font-medium" style={{ color: 'var(--text)' }}>{fmtEur(recapServicesHT + recapServicesTVA)}</td>
                      </tr>
                    )}
                    <tr style={{ background: 'var(--bg-subtle)' }}>
                      <td className="px-4 py-2.5 font-bold" style={{ color: 'var(--text)' }}>Total</td>
                      <td className="px-4 py-2.5 text-right font-bold" style={{ color: 'var(--primary-solid)' }}>{fmtEur(recapLocationHT + recapServicesHT)}</td>
                      <td className="px-4 py-2.5 text-right font-bold" style={{ color: 'var(--primary-solid)' }}>{fmtEur(recapLocationTVA + recapServicesTVA)}</td>
                      <td className="px-4 py-2.5 text-right font-bold" style={{ color: 'var(--primary-solid)' }}>{fmtEur(recapLocationHT + recapServicesHT + recapLocationTVA + recapServicesTVA)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            </div>
          )}
          </>
          )}

          {activeTab === 'terrain' && editing && (
            <div className="space-y-3">
              {!['signe', 'en_cours', 'termine'].includes(editing.statut) && (
                <div className="px-3 py-2 rounded-lg text-xs" style={{ background: '#FEF3C7', color: '#92400E' }}>
                  Le contrat doit être signé avant d'enregistrer une livraison.
                </div>
              )}

              {/* ── BL en attente sur tablette ── */}
              {bls.filter(b => b.statut === 'A_LIVRER').length > 0 && (
                <div>
                  <div className="text-xs font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>En attente sur tablette</div>
                  {bls.filter(b => b.statut === 'A_LIVRER').map(bl => (
                    <div key={bl.bl_id} className="flex items-center justify-between text-xs px-3 py-2 rounded-lg border mb-1"
                      style={{ borderColor: '#BFDBFE', background: '#EFF6FF' }}>
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-2 h-2 rounded-full" style={{ background: '#F59E0B' }}></span>
                        <span className="font-medium" style={{ color: '#1E40AF' }}>
                          {bl.type_mouvement === 'SORTIE' ? 'Sortie' : 'Retour'} — {bl.numero}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span style={{ color: '#6B7280' }}>{fmtDate(bl.date_prevue)}</span>
                        <button
                          data-testid={`bl-delete-${bl.bl_id}`}
                          title="Supprimer ce BL"
                          onClick={async () => {
                            if (!window.confirm(`Supprimer le BL ${bl.numero} ?`)) return;
                            try {
                              await api.supprimerBLV2(bl.bl_id);
                              toast.success('BL supprimé');
                              loadBls(editing.contrat_id);
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || 'Erreur suppression');
                            }
                          }}
                          className="p-1 rounded hover:bg-red-100 transition-colors"
                          style={{ color: '#DC2626' }}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Lignes : sorties à préparer + retours ── */}
              {(editing.lignes || []).filter(l => l.statut_ligne === 'active').map(ligne => {
                const sorties     = sortiesParLigne[ligne.ligne_id] || [];
                const canSortir   = ['signe', 'en_cours'].includes(editing.statut)
                  && ((ligne.qte_sortie || 0) + (pendingSortiesParLigne[ligne.ligne_id] || 0)) < ligne.quantite;
                const canReserver = ['signe', 'reserve', 'confirme'].includes(editing.statut)
                  && !sorties.length
                  && ((ligne.qte_sortie || 0) + (pendingSortiesParLigne[ligne.ligne_id] || 0)) < ligne.quantite;
                const dispos      = materielsDispo[ligne.ligne_id];
                const selMat      = blSel[ligne.ligne_id] || '';
                const selDate     = blSelDate[ligne.ligne_id] || '';
                const reserveId   = ligne.equipement_id_reserve || null;
                // Nom affiché du matériel réservé
                const reserveMat  = reserveId ? (dispos || []).find(m => m.materiel_id === reserveId) : null;
                const reserveLabel = reserveMat
                  ? [reserveMat.numero_parc, reserveMat.immatriculation, reserveMat.numero_serie].filter(Boolean).join(' · ') || reserveMat.nom
                  : reserveId;
                return (
                  <div key={ligne.ligne_id} className="py-2 border-b" style={{ borderColor: 'var(--border-light)' }} data-testid={`terrain-ligne-${ligne.ligne_id}`}>
                    {/* En-tête ligne */}
                    <div className="flex items-baseline justify-between mb-1.5">
                      <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{ligne.categorie_nom} — {ligne.gamme_nom}</span>
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{ligne.qte_sortie || 0}/{ligne.quantite} sorti(s)</span>
                    </div>

                    {/* Ligne unifiée : sélecteur matériel + Voir les locations + Réserver + Sortie */}
                    {(canReserver || canSortir) && !reserveId && (() => {
                      const selectedMat = selMat ? (dispos || []).find(m => m.materiel_id === selMat) : null;
                      const hasConflicts = selectedMat && (
                        (selectedMat.conflicts || []).length > 0 || selectedMat.statut === 'loue'
                      );
                      return (
                        <div className="mb-2 flex items-center gap-2 flex-wrap">
                          {/* Sélecteur unique */}
                          <select value={selMat || reserveId || ''}
                            onChange={e => setBlSel(p => ({ ...p, [ligne.ligne_id]: e.target.value }))}
                            data-testid={`terrain-mat-select-${ligne.ligne_id}`}
                            className="px-2 py-1 rounded-md border text-xs flex-1 min-w-0"
                            style={{ borderColor: 'var(--border)', color: 'var(--text)', background: 'var(--bg)' }}>
                            <option value="">{dispos ? 'Sélectionner un matériel…' : 'Chargement…'}</option>
                            {(dispos || []).map(m => {
                              const parts = [m.numero_parc, m.immatriculation, m.numero_serie].filter(Boolean);
                              const label = parts.length ? parts.join(' · ') : (m.nom || m.materiel_id);
                              const conflicts = m.conflicts || [];
                              const isOccupied = m.statut === 'loue' || conflicts.length > 0;
                              return <option key={m.materiel_id} value={m.materiel_id}>{label}{isOccupied ? ' ⚠ En location' : ''}</option>;
                            })}
                          </select>

                          {/* Voir les locations — uniquement si le matériel sélectionné a des conflits V2 */}
                          {hasConflicts && (() => {
                            const conflicts = (selectedMat?.conflicts || []).filter(c => c.contrat_numero);
                            const popKey = `loc-${ligne.ligne_id}`;
                            return (
                              <div className="relative">
                                <button type="button"
                                  onClick={() => setBlSel(p => ({ ...p, [popKey]: !p[popKey] }))}
                                  data-testid={`voir-locations-${ligne.ligne_id}`}
                                  className="text-xs px-2.5 py-1 rounded-md font-medium"
                                  style={{ color: '#B45309', border: '1px solid #FCD34D', background: '#FFFBEB' }}>
                                  Voir les locations
                                </button>
                                {blSel[popKey] && (
                                  <div className="absolute left-0 top-full mt-1 rounded-lg shadow-xl z-50 min-w-64"
                                    style={{ background: '#fff', border: '1px solid var(--border)' }}>
                                    {conflicts.length === 0 ? (
                                      <p className="px-3 py-2 text-xs" style={{ color: 'var(--text-muted)' }}>Informations de location indisponibles</p>
                                    ) : conflicts.map((c, i) => (
                                      <div key={i} className="px-3 py-2 border-b last:border-0 text-xs"
                                        style={{ borderColor: 'var(--border)' }}>
                                        <p className="font-semibold" style={{ color: 'var(--text)' }}>{c.contrat_numero}
                                          <span className="ml-1.5 font-normal text-xs" style={{ color: 'var(--text-muted)' }}>{c.statut}</span>
                                        </p>
                                        {c.client_nom && <p style={{ color: 'var(--text-muted)' }}>{c.client_nom}</p>}
                                        <p style={{ color: '#B45309' }}>{fmtDate(c.date_debut)} → {fmtDate(c.date_fin)}</p>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                          {/* Réserver */}
                          {canReserver && (
                            <button type="button"
                              onClick={() => { setBlSel(p => ({...p, [`loc-${ligne.ligne_id}`]: false})); if (selMat) handleReserver(ligne.ligne_id, selMat); else toast.error('Sélectionnez un matériel'); }}
                              disabled={reservingLine === ligne.ligne_id || !selMat}
                              data-testid={`reserver-btn-${ligne.ligne_id}`}
                              className="text-xs font-semibold px-3 py-1 rounded-md text-white disabled:opacity-40"
                              style={{ background: '#7C3AED' }}>
                              {reservingLine === ligne.ligne_id ? '…' : 'Réserver'}
                            </button>
                          )}

                          {/* Sortie */}
                          {canSortir && (<>
                            <input type="datetime-local" value={selDate}
                              onChange={e => setBlSelDate(p => ({ ...p, [ligne.ligne_id]: e.target.value }))}
                              data-testid={`terrain-date-${ligne.ligne_id}`}
                              className="px-2 py-1 rounded-md border text-xs w-40"
                              style={{ borderColor: 'var(--border)', color: 'var(--text)', background: 'var(--bg)' }} />
                            <button type="button" onClick={() => handlePreparerLigne(ligne.ligne_id, 'SORTIE', selMat || reserveId || '')}
                              disabled={savingBl || !(selMat || reserveId)}
                              data-testid={`terrain-sortie-btn-${ligne.ligne_id}`}
                              className="text-xs font-semibold px-3 py-1 rounded-md disabled:opacity-40"
                              style={{ color: '#1D4ED8', border: '1px solid #93C5FD' }}>
                              Sortie
                            </button>
                          </>)}
                        </div>
                      );
                    })()}

                    {/* Matériel déjà réservé → bouton Sortie directe (pas de sélecteur) */}
                    {canSortir && reserveId && sorties.length === 0 && (
                      <div className="mb-2 flex items-center gap-2 flex-wrap" data-testid={`terrain-reserved-row-${ligne.ligne_id}`}>
                        <span className="text-xs px-2 py-1 rounded-md font-medium"
                          style={{ background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}>
                          Réservé : {reserveLabel || reserveId}
                        </span>
                        <input type="datetime-local" value={selDate}
                          onChange={e => setBlSelDate(p => ({ ...p, [ligne.ligne_id]: e.target.value }))}
                          data-testid={`terrain-date-${ligne.ligne_id}`}
                          className="px-2 py-1 rounded-md border text-xs w-40"
                          style={{ borderColor: 'var(--border)', color: 'var(--text)', background: 'var(--bg)' }} />
                        <button type="button"
                          onClick={() => handlePreparerLigne(ligne.ligne_id, 'SORTIE', reserveId)}
                          disabled={savingBl}
                          data-testid={`terrain-sortie-btn-${ligne.ligne_id}`}
                          className="text-xs font-semibold px-3 py-1 rounded-md disabled:opacity-40"
                          style={{ color: '#1D4ED8', border: '1px solid #93C5FD' }}>
                          Sortie
                        </button>
                      </div>
                    )}

                    {/* Matériels déjà sortis → date/heure de retour + bouton Retour */}
                    {sorties.length > 0 && (
                      <div className="mt-1.5 space-y-1">
                        {sorties.map(s => {
                          // Date retour pré-remplie : date_fin_prevue de la ligne, 16h00 heure de l'agence
                          const retDef = (ligne.date_fin_prevue ? String(ligne.date_fin_prevue).slice(0, 10) : todayInTz(activeSocieteTimezone)) + 'T16:00';
                          const retDate = blRetourDate[s.equipement_id] ?? retDef;
                          return (
                          <div key={s.equipement_id} className="flex items-center gap-2 flex-wrap text-xs"
                            data-testid={`terrain-sortie-row-${s.equipement_id}`}>
                            <span className="flex-1 min-w-0 truncate" style={{ color: 'var(--text-muted)' }}>{s.nom}{s.numero_serie ? ` — ${s.numero_serie}` : ''}</span>
                            <input type="datetime-local" value={retDate}
                              onChange={e => setBlRetourDate(p => ({ ...p, [s.equipement_id]: e.target.value }))}
                              data-testid={`terrain-retour-date-${s.equipement_id}`}
                              className="px-2 py-1 rounded-md border text-xs w-40"
                              style={{ borderColor: 'var(--border)', color: 'var(--text)', background: 'var(--bg)' }} />
                            <button onClick={() => handlePreparerLigne(ligne.ligne_id, 'RETOUR', s.equipement_id, retDate)}
                              disabled={savingBl}
                              data-testid={`terrain-retour-btn-${s.equipement_id}`}
                              className="text-xs font-semibold px-3 py-1 rounded-md disabled:opacity-40"
                              style={{ color: '#166534', border: '1px solid #86EFAC' }}>
                              Retour
                            </button>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              <div>
                <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Historique des mouvements</div>
                {loadingBls && <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Chargement…</div>}
                {!loadingBls && bls.length === 0 && <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Aucun mouvement enregistré</div>}
                {bls.length > 0 && (
                <table className="w-full text-xs" data-testid="terrain-bl-history-table">
                  <thead>
                    <tr className="text-left border-b" style={{ borderColor: 'var(--border-light)', color: 'var(--text-muted)' }}>
                      <th className="py-1 pr-2 font-medium">Type</th>
                      <th className="py-1 pr-2 font-medium">Numéro</th>
                      <th className="py-1 pr-2 font-medium">Matériel</th>
                      <th className="py-1 pr-2 font-medium">Date</th>
                      <th className="py-1 pr-2 font-medium">Heure</th>
                      <th className="py-1 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {bls.flatMap(bl => ((bl.lignes && bl.lignes.length > 0) ? bl.lignes : [null]).map((l, li) => (
                      <tr key={`${bl.bl_id}-${li}`} className="border-b last:border-0" style={{ borderColor: 'var(--border-light)' }}
                        data-testid={`terrain-bl-row-${bl.bl_id}`}>
                        <td className="py-1.5 pr-2">{bl.type_mouvement === 'SORTIE' ? 'Sortie' : 'Retour'}</td>
                        <td className="py-1.5 pr-2">{bl.numero}</td>
                        <td className="py-1.5 pr-2">{l ? [l.equipement_nom, l.numero_parc].filter(Boolean).join(' — ') || '—' : '—'}</td>
                        <td className="py-1.5 pr-2">{(bl.date_effective || bl.date_prevue) ? new Date(bl.date_effective || bl.date_prevue).toLocaleDateString('fr-FR', { timeZone: activeSocieteTimezone }) : '—'}</td>
                        <td className="py-1.5 pr-2">{(bl.date_effective || bl.date_prevue) ? new Date(bl.date_effective || bl.date_prevue).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: activeSocieteTimezone }) : '—'}</td>
                        <td className="py-1.5 text-right">
                          {bl.statut === 'LIVRE' && (
                            <button
                              data-testid={`bl-voir-controles-${bl.bl_id}`}
                              title="Voir l'inspection"
                              onClick={async () => {
                                setVoirControlesBlId(bl.bl_id);
                                setLoadingControles(true);
                                try {
                                  const [ctlRes, blRes] = await Promise.all([
                                    api.getControlesV2(bl.bl_id),
                                    api.terrainGetBLV2(bl.bl_id),
                                  ]);
                                  setControlesBl(ctlRes.data || []);
                                  setVoirBlDetail(blRes.data || null);
                                } catch { toast.error('Impossible de charger les contrôles'); }
                                finally { setLoadingControles(false); }
                              }}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border"
                              style={{ borderColor: '#BFDBFE', background: '#EFF6FF', color: '#1D4ED8' }}>
                              <Eye size={11} /> Voir
                            </button>
                          )}
                        </td>
                      </tr>
                    )))}
                  </tbody>
                </table>
                )}
              </div>
            </div>
          )}

          {activeTab === 'avenants' && editing && (
            <div className="space-y-4">
              {['signe', 'en_cours'].includes(editing.statut) ? (
                avenantForm ? (
                  <div className="p-3 rounded-lg space-y-2" style={{ background: '#F5F3FF' }}>
                    <select value={avenantForm.type_avenant} onChange={e => setAvenantForm(f => ({ ...emptyAvenantForm(), type_avenant: e.target.value }))}
                      data-testid="avenant-type-select" className="w-full px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }}>
                      {Object.entries(TYPES_AVENANT_CREATION).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>

                    {avenantForm.type_avenant === 'AJOUT_MATERIEL' && (
                      <div className="flex gap-2">
                        <button onClick={() => setAvenantForm(f => ({ ...f, ajoutMode: 'existante' }))} data-testid="avenant-ajout-mode-existante"
                          className="flex-1 text-xs font-semibold py-1.5 rounded-md" style={{ background: avenantForm.ajoutMode === 'existante' ? 'var(--card)' : 'transparent', border: '1px solid var(--border)' }}>
                          Ligne existante
                        </button>
                        <button onClick={() => setAvenantForm(f => ({ ...f, ajoutMode: 'nouvelle' }))} data-testid="avenant-ajout-mode-nouvelle"
                          className="flex-1 text-xs font-semibold py-1.5 rounded-md" style={{ background: avenantForm.ajoutMode === 'nouvelle' ? 'var(--card)' : 'transparent', border: '1px solid var(--border)' }}>
                          Nouvelle gamme
                        </button>
                      </div>
                    )}

                    {(avenantForm.type_avenant !== 'AJOUT_MATERIEL' || avenantForm.ajoutMode === 'existante') && (
                      <select value={avenantForm.ligne_id} onChange={e => setAvenantForm(f => ({ ...f, ligne_id: e.target.value }))}
                        data-testid="avenant-ligne-select" className="w-full px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }}>
                        <option value="">Ligne concernée…</option>
                        {(editing.lignes || []).filter(l => l.statut_ligne === 'active').map(l => (
                          <option key={l.ligne_id} value={l.ligne_id}>{l.categorie_nom} — {l.gamme_nom} (qté {l.quantite})</option>
                        ))}
                      </select>
                    )}

                    {avenantForm.type_avenant === 'PROLONGATION' && (
                      <input type="date" placeholder="Nouvelle date de fin" value={avenantForm.nouvelle_date_fin}
                        onChange={e => setAvenantForm(f => ({ ...f, nouvelle_date_fin: e.target.value }))}
                        data-testid="avenant-nouvelle-date-fin-input" className="w-full px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }} />
                    )}
                    {avenantForm.type_avenant === 'AJUSTEMENT_TARIF' && (
                      <input type="number" step="0.01" placeholder="Nouveau prix/jour" value={avenantForm.nouveau_prix_jour}
                        onChange={e => setAvenantForm(f => ({ ...f, nouveau_prix_jour: e.target.value }))}
                        data-testid="avenant-nouveau-prix-input" className="w-full px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }} />
                    )}
                    {(avenantForm.type_avenant === 'RETRAIT_MATERIEL' || (avenantForm.type_avenant === 'AJOUT_MATERIEL' && avenantForm.ajoutMode === 'existante')) && (
                      <input type="number" min={0} placeholder="Nouvelle quantité" value={avenantForm.nouvelle_quantite}
                        onChange={e => setAvenantForm(f => ({ ...f, nouvelle_quantite: e.target.value }))}
                        data-testid="avenant-nouvelle-quantite-input" className="w-full px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }} />
                    )}

                    {avenantForm.type_avenant === 'AJOUT_MATERIEL' && avenantForm.ajoutMode === 'nouvelle' && (
                      <>
                        <select value={avenantForm.nouvelle_categorie_id} onChange={e => { setAvenantForm(f => ({ ...f, nouvelle_categorie_id: e.target.value, nouvelle_gamme_id: '' })); loadGammes(e.target.value); }}
                          data-testid="avenant-nouvelle-categorie-select" className="w-full px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }}>
                          <option value="">Catégorie…</option>
                          {categories.map(c => <option key={c.categorie_id} value={c.categorie_id}>{c.nom}</option>)}
                        </select>
                        <select value={avenantForm.nouvelle_gamme_id} onChange={e => setAvenantForm(f => ({ ...f, nouvelle_gamme_id: e.target.value }))} disabled={!avenantForm.nouvelle_categorie_id}
                          data-testid="avenant-nouvelle-gamme-select" className="w-full px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }}>
                          <option value="">Gamme…</option>
                          {(gammesByCat[avenantForm.nouvelle_categorie_id] || []).map(g => <option key={g.gamme_id} value={g.gamme_id}>{g.nom}</option>)}
                        </select>
                        <div className="grid grid-cols-3 gap-2">
                          <input type="number" min={1} placeholder="Qté" value={avenantForm.nouvelle_ligne_quantite}
                            onChange={e => setAvenantForm(f => ({ ...f, nouvelle_ligne_quantite: e.target.value }))}
                            data-testid="avenant-nouvelle-ligne-quantite-input" className="px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }} />
                          <input type="date" value={avenantForm.nouvelle_ligne_date_debut}
                            onChange={e => setAvenantForm(f => ({ ...f, nouvelle_ligne_date_debut: e.target.value }))}
                            data-testid="avenant-nouvelle-ligne-date-debut-input" className="px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }} />
                          <input type="date" value={avenantForm.nouvelle_ligne_date_fin}
                            onChange={e => setAvenantForm(f => ({ ...f, nouvelle_ligne_date_fin: e.target.value }))}
                            data-testid="avenant-nouvelle-ligne-date-fin-input" className="px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }} />
                        </div>
                      </>
                    )}

                    <input type="date" placeholder="Date d'effet" value={avenantForm.date_effet}
                      onChange={e => setAvenantForm(f => ({ ...f, date_effet: e.target.value }))}
                      data-testid="avenant-date-effet-input" className="w-full px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }} />
                    <input placeholder="Motif" value={avenantForm.motif}
                      onChange={e => setAvenantForm(f => ({ ...f, motif: e.target.value }))}
                      data-testid="avenant-motif-input" className="w-full px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }} />

                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setAvenantForm(null)} data-testid="avenant-cancel-btn" className="text-xs px-3 py-1.5 rounded-lg" style={{ color: 'var(--text-muted)' }}>Annuler</button>
                      <button onClick={submitAvenant} disabled={savingAvenant} data-testid="avenant-submit-btn"
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white" style={{ background: 'var(--primary-solid)' }}>
                        Créer l'avenant (brouillon)
                      </button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setAvenantForm(emptyAvenantForm())} data-testid="avenant-new-btn"
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg" style={{ background: '#F5F3FF', color: '#6D28D9' }}>
                    <Plus size={13} /> Nouvel avenant
                  </button>
                )
              ) : null}

              <div>
                <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Chronologie contractuelle</div>
                {/* Devis/Contrat initial — toujours en tête, avenants en dessous.
                    Tant que rien n'est signé, c'est un DEVIS (pas un contrat) :
                    libellé, mention d'envoi et bouton PDF adaptés. */}
                <div className="flex items-center justify-between text-xs px-3 py-2 rounded-lg border mb-2 gap-2" style={{ borderColor: '#C7D2FE', background: '#EEF2FF' }} data-testid="contrat-initial-row">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold" style={{ color: 'var(--text)' }}>{editing.numero || editing.contrat_id}</span>
                      <span style={{ color: 'var(--text-muted)' }}>
                        — {editing.date_signature ? 'Contrat initial' : 'Devis initial'}
                        {(recapLocationHT + recapServicesHT + recapLocationTVA + recapServicesTVA) > 0
                          ? ` · ${fmtEur(recapLocationHT + recapServicesHT + recapLocationTVA + recapServicesTVA)} TTC` : ''}
                      </span>
                    </div>
                    {editing.date_signature ? (
                      <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                        signé le {fmtDate(editing.date_signature)}
                        {editing.validation?.signataire_nom ? ` par ${editing.validation.signataire_nom}` : ''}
                      </div>
                    ) : editing.validation?.signataire_nom ? (
                      <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                        en attente de signature par {editing.validation.signataire_nom}
                      </div>
                    ) : editing.validation?.devis_email_envoye ? (
                      <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                        devis envoyé{editing.validation.devis_email_at ? ` le ${fmtDate(editing.validation.devis_email_at)}` : ''}
                        {editing.validation.devis_email_destinataire ? ` à ${editing.validation.devis_email_destinataire}` : ''}
                      </div>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <StatutBadge statut={editing.statut} />
                    <button onClick={() => { api.getContratV2PDF(editing.contrat_id).then(r => { const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })); window.open(url, '_blank'); }).catch(() => toast.error('PDF indisponible')); }}
                      data-testid="contrat-initial-pdf-btn"
                      className="text-xs px-2 py-0.5 rounded font-semibold"
                      style={{ background: editing.date_signature ? '#1D4ED8' : '#EFF6FF', color: editing.date_signature ? '#fff' : '#1D4ED8' }}>
                      {editing.date_signature ? 'Afficher PDF' : 'PDF devis'}
                    </button>
                    {editing.date_signature && (
                      <button
                        data-testid="contrat-initial-envoyer-mail-btn"
                        onClick={() => setEmailContratSigneOpen(true)}
                        className="flex items-center gap-1 text-xs px-2 py-0.5 rounded font-semibold text-white"
                        style={{ background: '#1D4ED8' }}>
                        <Mail size={11} /> Envoyer par mail
                      </button>
                    )}
                  </div>
                </div>
                {loadingAvenants && <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Chargement…</div>}
                {!loadingAvenants && avenants.length === 0 && <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Aucun avenant enregistré</div>}
                <div className="space-y-2">
                  {avenants.map(av => {
                    const s = STATUTS_AVENANT[av.statut] || STATUTS_AVENANT.brouillon;
                    const expanded = expandedAvenants.has(av.avenant_id);
                    const hasSnapshot = (av.snapshot_lignes?.length > 0) || (av.snapshot_services?.length > 0);
                    const signInfo = av.payload?.signed_at
                      ? { label: 'signé', date: fmtDate(av.payload.signed_at), par: av.payload.signataire_nom || '' }
                      : av.date_validation
                      ? { label: 'validé', date: fmtDate(av.date_validation), par: av.payload?.valide_par_nom || '' }
                      : null;
                    return (
                      <div key={av.avenant_id} className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border-light)' }} data-testid={`avenant-row-${av.avenant_id}`}>
                        {/* En-tête */}
                        <div className="flex items-center justify-between text-xs px-3 py-2 gap-2" style={{ background: 'var(--bg-secondary)' }}>
                          <button type="button" onClick={() => setExpandedAvenants(prev => { const n = new Set(prev); n.has(av.avenant_id) ? n.delete(av.avenant_id) : n.add(av.avenant_id); return n; })}
                            className="flex items-start gap-2 flex-1 min-w-0 text-left">
                            <ChevronDown size={13} style={{ flexShrink: 0, marginTop: 2, transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="font-semibold" style={{ color: 'var(--text)' }}>{av.numero}</span>
                                <span style={{ color: 'var(--text-muted)' }}>— {TYPES_AVENANT[av.type_avenant] || av.type_avenant} · {fmtDate(av.date_effet)}{av.motif ? ` · ${av.motif}` : ''}</span>
                              </div>
                              {signInfo && (
                                <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                                  {signInfo.label} le {signInfo.date}{signInfo.par ? ` par ${signInfo.par}` : ''}
                                </div>
                              )}
                            </div>
                          </button>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="px-2 py-0.5 rounded-full font-semibold" style={{ background: s.bg, color: s.color }}>{s.label}</span>
                            {av.statut === 'valide' && (
                              <button onClick={() => { api.getContratV2PDF(editing.contrat_id).then(r => { const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })); window.open(url, '_blank'); }).catch(() => toast.error('PDF indisponible')); }}
                                className="text-xs px-2 py-0.5 rounded font-semibold" style={{ background: '#EFF6FF', color: '#1D4ED8' }}>
                                PDF
                              </button>
                            )}
                            {av.statut === 'brouillon' && (
                              <>
                                <button onClick={() => openMailDevisModalV2(av)} data-testid={`avenant-envoyer-devis-btn-${av.avenant_id}`}
                                  className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded"
                                  style={{ background: '#EFF6FF', color: '#1D4ED8' }}>
                                  <Send size={11} /> Envoyer devis
                                </button>
                                {av.payload?.devis_email_envoye && (
                                  <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium"
                                    style={{ background: '#EFF6FF', color: '#1D4ED8' }}>
                                    <Mail size={9} /> {av.payload.devis_email_destinataire || '—'}
                                  </span>
                                )}
                                <button onClick={() => openSigMode('tablette', av.avenant_id)} className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded" style={{ background: '#F0FDF4', color: '#166534' }}>
                                  <FileSignature size={11} /> Signer
                                </button>
                                <button onClick={() => annulerAvenant(av.avenant_id)} data-testid={`avenant-annuler-btn-${av.avenant_id}`} className="font-semibold text-xs" style={{ color: '#991B1B' }}>Annuler</button>
                              </>
                            )}
                            {av.statut === 'en_attente_signature' && (
                              <button onClick={() => openSigMode('tablette', av.avenant_id)} className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded" style={{ background: '#FEF3C7', color: '#92400E' }}>
                                <FileSignature size={11} /> Signer
                              </button>
                            )}
                          </div>
                        </div>
                        {/* Snapshot dépliable */}
                        {expanded && hasSnapshot && (
                          <div className="px-3 py-2 space-y-2 border-t" style={{ borderColor: 'var(--border-light)' }}>
                            {av.snapshot_lignes?.length > 0 && (
                              <div>
                                <div className="text-[10px] font-semibold uppercase tracking-wide mb-1" style={{ color: 'var(--text-muted)' }}>Matériels</div>
                                <table className="w-full text-xs">
                                  <thead><tr style={{ color: 'var(--text-muted)' }}><th className="text-left pb-1">Catégorie</th><th className="text-left pb-1">Gamme</th><th className="pb-1 text-right">Qté</th><th className="pb-1 text-right">Début</th><th className="pb-1 text-right">Fin</th><th className="pb-1 text-right">Statut</th></tr></thead>
                                  <tbody>
                                    {av.snapshot_lignes.map(l => (
                                      <tr key={l.ligne_id} style={{ opacity: l.statut_ligne === 'supprimee' ? 0.5 : 1 }}>
                                        <td className="py-0.5">{l.categorie_nom || '—'}</td>
                                        <td className="py-0.5">{l.gamme_nom || '—'}</td>
                                        <td className="py-0.5 text-right">{l.quantite}</td>
                                        <td className="py-0.5 text-right">{fmtDate(l.date_debut_prevue)}</td>
                                        <td className="py-0.5 text-right">{fmtDate(l.date_fin_prevue)}</td>
                                        <td className="py-0.5 text-right" style={{ color: l.statut_ligne === 'supprimee' ? '#DC2626' : '#166534' }}>{l.statut_ligne}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                            {av.snapshot_services?.length > 0 && (
                              <div>
                                <div className="text-[10px] font-semibold uppercase tracking-wide mb-1" style={{ color: 'var(--text-muted)' }}>Prestations</div>
                                <table className="w-full text-xs">
                                  <thead><tr style={{ color: 'var(--text-muted)' }}><th className="text-left pb-1">Réf.</th><th className="text-left pb-1">Désignation</th><th className="pb-1 text-right">Qté</th><th className="pb-1 text-right">PU HT</th></tr></thead>
                                  <tbody>
                                    {av.snapshot_services.map(s => (
                                      <tr key={s.service_id}>
                                        <td className="py-0.5 font-mono">{s.article_reference || '—'}</td>
                                        <td className="py-0.5">{s.designation || '—'}</td>
                                        <td className="py-0.5 text-right">{s.quantite}</td>
                                        <td className="py-0.5 text-right">{fmtEur(s.prix_unitaire_ht)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        )}
                        {expanded && !hasSnapshot && (
                          <div className="px-3 py-2 text-xs border-t" style={{ color: 'var(--text-muted)', borderColor: 'var(--border-light)' }}>
                            Aucun détail disponible pour cet avenant.
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ══ Onglet DOCUMENTS (pièces déposées uniquement — pattern commandes d'achat) ══ */}
          {activeTab === 'documents' && editing && (
            <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }} data-testid="locations-v2-documents-tab">
              <div className="px-4 py-2.5 border-b flex items-center gap-2"
                style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
                <Upload size={13} style={{ color: 'var(--primary-solid)' }} />
                <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
                  Pièces déposées par l'utilisateur{docCount > 0 ? ` (${docCount})` : ''}
                </p>
              </div>
              <div className="p-3">
                <DocumentsTab
                  entityId={editing.contrat_id}
                  listFn={() => api.getContratDocumentsTab(editing.contrat_id)}
                  uploadFn={(fd) => api.uploadContratDocumentTab(editing.contrat_id, fd)}
                  updateFn={(docId, data) => api.patchContratDocumentTab(editing.contrat_id, docId, data)}
                  deleteFn={(docId) => api.deleteContratDocumentTab(editing.contrat_id, docId)}
                  downloadFn={(docId) => api.downloadContratDocumentTab(editing.contrat_id, docId)}
                  onCountChange={setDocCount}
                />
              </div>
            </div>
          )}

          {activeTab === 'suspensions' && editing && (
            <div className="space-y-4">
              {['en_cours', 'termine'].includes(editing.statut) ? (
                suspensionForm ? (
                  <div className="p-3 rounded-lg space-y-2" style={{ background: '#FEF3C7' }}>
                    <select value={suspensionForm.ligne_contrat_id} onChange={e => setSuspensionForm(f => ({ ...f, ligne_contrat_id: e.target.value }))}
                      data-testid="suspension-ligne-select" className="w-full px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }}>
                      <option value="">Ligne concernée (matériel livré)…</option>
                      {(editing.lignes || []).filter(l => l.statut_ligne === 'active' && (l.qte_sortie > 0 || l.qte_livree > 0)).map(l => (
                        <option key={l.ligne_id} value={l.ligne_id}>{l.categorie_nom} — {l.gamme_nom} ({l.qte_sortie > 0 ? `${l.qte_sortie} sorti(s)` : 'restitué'})</option>
                      ))}
                    </select>
                    <select value={suspensionForm.motif_id} onChange={e => setSuspensionForm(f => ({ ...f, motif_id: e.target.value }))}
                      data-testid="suspension-motif-select" className="w-full px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }}>
                      <option value="">Motif de suspension…</option>
                      {motifsSuspension.map(m => <option key={m.motif_id} value={m.motif_id}>{m.libelle}</option>)}
                    </select>
                    <div className="grid grid-cols-2 gap-2">
                      <input type="date" placeholder="Date début" value={suspensionForm.date_debut}
                        onChange={e => setSuspensionForm(f => ({ ...f, date_debut: e.target.value }))}
                        data-testid="suspension-date-debut-input" className="px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }} />
                      <input type="date" placeholder="Date fin" value={suspensionForm.date_fin}
                        onChange={e => setSuspensionForm(f => ({ ...f, date_fin: e.target.value }))}
                        data-testid="suspension-date-fin-input" className="px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }} />
                    </div>
                    <div>
                      <label className="text-xs" style={{ color: 'var(--text-muted)' }}>Taux de facturation pendant la suspension (%)</label>
                      <input type="number" min={0} max={100} step="1" value={suspensionForm.taux_facturation}
                        onChange={e => setSuspensionForm(f => ({ ...f, taux_facturation: e.target.value }))}
                        data-testid="suspension-taux-input" className="w-full px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }} />
                    </div>
                    <input placeholder="Description (optionnel)" value={suspensionForm.description}
                      onChange={e => setSuspensionForm(f => ({ ...f, description: e.target.value }))}
                      data-testid="suspension-description-input" className="w-full px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }} />

                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setSuspensionForm(null)} data-testid="suspension-cancel-btn" className="text-xs px-3 py-1.5 rounded-lg" style={{ color: 'var(--text-muted)' }}>Annuler</button>
                      <button onClick={submitSuspension} disabled={savingSuspension} data-testid="suspension-submit-btn"
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white" style={{ background: 'var(--primary-solid)' }}>
                        Déclarer la suspension
                      </button>
                    </div>
                  </div>
                ) : (
                  (editing.lignes || []).some(l => l.statut_ligne === 'active' && (l.qte_sortie > 0 || l.qte_livree > 0)) ? (
                    <button onClick={() => setSuspensionForm(emptySuspensionForm())} data-testid="suspension-new-btn"
                      className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg" style={{ background: '#FEF3C7', color: '#92400E' }}>
                      <Plus size={13} /> Nouvelle suspension
                    </button>
                  ) : (
                    <div className="px-3 py-2 rounded-lg text-xs" style={{ background: '#FEF3C7', color: '#92400E' }}>
                      Aucun matériel livré sur ce contrat — impossible de suspendre.
                    </div>
                  )
                )
              ) : (
                <div className="px-3 py-2 rounded-lg text-xs" style={{ background: '#FEF3C7', color: '#92400E' }}>
                  Les suspensions ne sont possibles que sur un contrat en cours ou terminé.
                </div>
              )}

              <div>
                <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Historique des suspensions</div>
                {loadingSuspensions && <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Chargement…</div>}
                {!loadingSuspensions && suspensions.length === 0 && <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Aucune suspension enregistrée</div>}
                <div className="space-y-1">
                  {suspensions.map(sp => (
                    <div key={sp.suspension_id} className="flex items-center justify-between text-xs px-2 py-1.5 rounded-md border" style={{ borderColor: 'var(--border-light)' }} data-testid={`suspension-row-${sp.suspension_id}`}>
                      <div>
                        <span className="font-semibold" style={{ color: 'var(--text)' }}>{sp.numero}</span>{' '}
                        <span style={{ color: 'var(--text-muted)' }}>
                          — {sp.motif_libelle} · {fmtDate(sp.date_debut)} → {fmtDate(sp.date_fin)} · {sp.nb_jours_suspendus}j · exonéré {fmtEur(sp.montant_exonere_estime)} ({sp.taux_facturation}% facturé)
                          {sp.description ? ` · ${sp.description}` : ''}
                        </span>
                      </div>
                      <button onClick={() => deleteSuspension(sp.suspension_id)} data-testid={`suspension-delete-btn-${sp.suspension_id}`} className="font-semibold" style={{ color: '#991B1B' }}>Supprimer</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'facturation' && editing && (
            <div className="space-y-4">
              {['signe', 'en_cours', 'termine'].includes(editing.statut) ? (
                <div className="p-3 rounded-lg space-y-2" style={{ background: '#EEF2FF' }}>
                  <div className="text-xs font-semibold" style={{ color: '#3730A3' }}>
                    Facturer le reste à facturer jusqu'à aujourd'hui (ou une date de clôture) — possible même avant la livraison, sur les dates prévues du contrat
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <label className="text-xs" style={{ color: 'var(--text-muted)' }}>Date de fin de période (optionnel — sinon aujourd'hui, ou la fin du contrat s'il démarre plus tard)</label>
                      <input type="date" value={facturationPeriodeFin} onChange={e => { setFacturationPeriodeFin(e.target.value); setPreviewFacture(null); }}
                        data-testid="facturation-periode-fin-input" className="w-full px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }} />
                    </div>
                    <div className="flex-1">
                      <label className="text-xs" style={{ color: 'var(--text-muted)' }}>Référence client (PO / BdC) — optionnel</label>
                      <input type="text" value={facturationRefClient} onChange={e => setFacturationRefClient(e.target.value)} maxLength={100}
                        placeholder="Ex : PO-2026-1234"
                        data-testid="facturation-ref-client-input" className="w-full px-2 py-1.5 rounded-md border text-xs" style={{ borderColor: 'var(--border)' }} />
                    </div>
                    <button onClick={previewFacturation} disabled={loadingPreview} data-testid="facturation-preview-btn"
                      className="text-xs font-semibold px-3 py-2 rounded-lg text-white" style={{ background: '#4338CA' }}>
                      {loadingPreview ? 'Calcul…' : 'Prévisualiser'}
                    </button>
                  </div>

                  {previewFacture && (() => {
                    const debutLignes = (editing.lignes || []).map(l => l.date_debut_prevue).filter(Boolean).sort()[0];
                    const debutContrat = debutLignes || editing.date_debut_initiale;
                    return (
                    <div className="p-3 rounded-lg space-y-2 mt-2" style={{ background: 'var(--card)', border: '1px solid var(--border)' }} data-testid="facturation-preview-result">
                      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        Période : {fmtDate(previewFacture.periode_debut || debutContrat)} → {fmtDate(previewFacture.periode_fin)}
                      </div>
                      <div className="space-y-1">
                        {previewFacture.lignes.map((l, i) => {
                          const desc = l.description || '';
                          const nom = l.gamme_nom || '';
                          let texte;
                          if (desc.includes('\n')) texte = desc;
                          else if (desc && nom && desc !== nom) texte = `${nom}\n${desc}`;
                          else texte = desc || nom;
                          return (
                          <div key={i} className="flex items-start justify-between gap-2 text-xs px-2 py-1 rounded-md" style={{ background: 'var(--bg-subtle)' }}>
                            <span style={{ color: 'var(--text)', whiteSpace: 'pre-line' }}>{texte}</span>
                            <span className="font-semibold whitespace-nowrap" style={{ color: l.is_assurance ? 'var(--text)' : (Number(l.prix_jour) < 0 ? '#DC2626' : 'var(--text)') }}>
                              {fmtEur(l.is_assurance ? l.prix_jour : (Number(l.nb_jours || 0) * Number(l.prix_jour || 0)))}
                            </span>
                          </div>
                          );
                        })}
                      </div>
                      <div className="flex items-center justify-between text-sm font-bold pt-1" style={{ borderTop: '1px solid var(--border-light)', color: 'var(--text)' }}>
                        <span>Total HT</span>
                        <span data-testid="facturation-preview-total">{fmtEur(previewFacture.total_ht)}</span>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => setPreviewFacture(null)} data-testid="facturation-preview-cancel-btn" className="text-xs px-3 py-1.5 rounded-lg" style={{ color: 'var(--text-muted)' }}>Annuler</button>
                        <button onClick={confirmFacturation} disabled={confirmingFacture} data-testid="facturation-confirm-btn"
                          className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white" style={{ background: '#166534' }}>
                          {confirmingFacture ? 'Envoi…' : 'Confirmer & envoyer à Pennylane'}
                        </button>
                      </div>
                    </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="px-3 py-2 rounded-lg text-xs" style={{ background: '#EEF2FF', color: '#3730A3' }}>
                  La facturation n'est possible que sur un contrat en cours ou terminé.
                </div>
              )}

              <div>
                <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Historique des factures</div>
                {loadingFactures && <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Chargement…</div>}
                {!loadingFactures && factures.length === 0 && <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Aucune facture émise sur ce contrat</div>}
                <div className="space-y-1">
                  {factures.map(f => (
                    <div key={f.facture_id} className="flex items-center justify-between text-xs px-2 py-1.5 rounded-md border" style={{ borderColor: 'var(--border-light)' }} data-testid={`facture-row-${f.facture_id}`}>
                      <div>
                        <span className="font-semibold" style={{ color: 'var(--text)' }}>{f.ext_invoice_number || f.facture_id}</span>{' '}
                        <span style={{ color: 'var(--text-muted)' }}>
                          — {fmtDate(f.periode_debut)} → {fmtDate(f.periode_fin)} · {fmtEur(f.montant_ttc)} TTC
                        </span>
                        {f.ref_client && (
                          <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>Réf. Client : {f.ref_client}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 rounded-full font-semibold" style={{ background: '#DBEAFE', color: '#1E40AF' }}>{f.statut_paiement}</span>
                        <button
                          data-testid={`facture-pl-panel-btn-${f.facture_id}`}
                          title="Ouvrir le panel Pennylane"
                          onClick={() => openPlDraftPanel(f)}
                          className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border"
                          style={{ borderColor: '#BFDBFE', background: '#EFF6FF', color: '#1D4ED8' }}>
                          <FileText size={11} /> Pennylane
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'activites' && editing && (
            <div style={{ padding: '4px 0' }}>
              <ActivitesFichePanel
                record_type="location_v2"
                record_id={editing.contrat_id}
                record_label={editing.numero}
                type_document="location_v2"
                numero_document={editing.numero}
                created_by_nom={editing.created_by_nom ?? null}
              />
            </div>
          )}
        </div>

      {/* ─── Modale envoi devis V2 par email — DANS le SidePanel (pattern V1 : évite pointer-events/focus-trap Radix) ─── */}

      {/* ─── Modale signature V2 — DANS le SidePanel (pattern V1) ─── */}
      {showSigModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden" style={{ background: 'var(--card)' }}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
              <div className="flex items-center gap-2">
                {sigMode === 'tablette' && <Tablet size={16} style={{ color: '#6366F1' }} />}
                {sigMode === 'ordinateur' && <Monitor size={16} style={{ color: '#6366F1' }} />}
                {sigMode === 'mail' && <Mail size={16} style={{ color: '#6366F1' }} />}
                {(sigMode === 'lien' || sigMode === 'relien') && <LinkIcon size={16} style={{ color: '#6366F1' }} />}
                {sigMode === 'recue' && <CheckCircle2 size={16} style={{ color: '#16A34A' }} />}
                <span className="font-semibold text-sm" style={{ color: 'var(--text)' }}>
                  {sigMode === 'tablette' && 'Signature tablette terrain'}
                  {sigMode === 'ordinateur' && 'Signature ordinateur'}
                  {sigMode === 'mail' && 'Envoi PDF par mail'}
                  {(sigMode === 'lien' || sigMode === 'relien') && 'Lien de signature web'}
                  {sigMode === 'recue' && 'Confirmer signature reçue'}
                </span>
              </div>
              <button onClick={() => setShowSigModal(false)} className="p-1.5 rounded-lg hover:bg-gray-100"><XIcon size={16} /></button>
            </div>

            <div className="px-6 py-5 space-y-4">
              {/* ── Champs communs : nom + email ─────────────────────── */}
              {(sigMode !== 'recue') && (
                <div>
                  <label className="text-xs font-semibold block mb-1" style={{ color: 'var(--text-secondary)' }}>Nom du signataire</label>
                  <input value={signaireNom} onChange={e => setSignaireNom(e.target.value)} placeholder="Prénom Nom"
                    data-testid="sig-nom-input" className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border)' }} />
                </div>
              )}
              {(sigMode === 'mail' || sigMode === 'lien' || sigMode === 'relien') && (
                <>
                  {/* Sélecteur de contact (combobox non-creatable) */}
                  {contacts.filter(c => c.email || c.email_pro).length > 0 && (
                    <div style={{ position: 'relative' }}>
                      <label className="text-xs font-semibold block mb-1" style={{ color: 'var(--text-secondary)' }}>Choisir un contact</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type="text"
                          value={sigContactSearch}
                          onChange={e => { setSigContactSearch(e.target.value); setSigContactOpen(true); }}
                          onFocus={() => setSigContactOpen(true)}
                          onBlur={() => setTimeout(() => setSigContactOpen(false), 160)}
                          placeholder="Rechercher un contact…"
                          data-testid="sig-contact-search"
                          className="w-full px-3 py-2 rounded-lg border text-sm pr-8"
                          style={{ borderColor: 'var(--border)' }}
                        />
                        <ChevronDown size={13} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
                      </div>
                      {sigContactOpen && (
                        <div className="absolute left-0 right-0 rounded-xl border bg-white shadow-xl z-50 overflow-y-auto"
                          style={{ top: '100%', marginTop: 4, maxHeight: 180, borderColor: 'var(--border)' }}>
                          {contacts
                            .filter(c => {
                              const email = c.email || c.email_pro || '';
                              if (!email) return false;
                              if (!sigContactSearch) return true;
                              const q = sigContactSearch.toLowerCase();
                              return ([c.prenom, c.nom].filter(Boolean).join(' ') + ' ' + email).toLowerCase().includes(q);
                            })
                            .map((c, i, arr) => {
                              const nom = [c.prenom, c.nom].filter(Boolean).join(' ') || 'Sans nom';
                              const email = c.email || c.email_pro || '';
                              return (
                                <div key={c.personne_id || email}
                                  onMouseDown={e => {
                                    e.preventDefault();
                                    setSignaireNom(nom);
                                    setSigEmail(email);
                                    setSigContactSearch(`${nom} <${email}>`);
                                    setSigContactOpen(false);
                                  }}
                                  className="flex items-center gap-2 px-4 py-2.5 hover:bg-indigo-50 cursor-pointer text-xs"
                                  style={{ borderBottom: i < arr.length - 1 ? '1px solid #F3F4F6' : 'none' }}
                                  data-testid={`sig-contact-option-${i}`}>
                                  <User size={13} style={{ color: '#6366F1', flexShrink: 0 }} />
                                  <div>
                                    <div className="font-semibold" style={{ color: 'var(--text)' }}>{nom}</div>
                                    <div style={{ color: 'var(--text-muted)' }}>{email}</div>
                                  </div>
                                </div>
                              );
                            })
                          }
                          {contacts.filter(c => c.email || c.email_pro).length === 0 && (
                            <p className="text-xs px-4 py-3" style={{ color: 'var(--text-muted)' }}>Aucun contact avec email</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <div>
                    <label className="text-xs font-semibold block mb-1" style={{ color: 'var(--text-secondary)' }}>Email du signataire</label>
                    <input type="email" value={sigEmail} onChange={e => setSigEmail(e.target.value)} placeholder="contact@exemple.fr"
                      data-testid="sig-email-input" className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border)' }} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold block mb-1" style={{ color: 'var(--text-secondary)' }}>Sujet (optionnel)</label>
                    <input value={sigSujet} onChange={e => setSigSujet(e.target.value)} placeholder="Contrat à signer…"
                      data-testid="sig-sujet-input"
                      className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border)' }} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold block mb-1" style={{ color: 'var(--text-secondary)' }}>Message (optionnel)</label>
                    <textarea value={sigCorps} onChange={e => setSigCorps(e.target.value)} rows={3}
                      placeholder="Bonjour, veuillez trouver ci-joint le contrat à signer…"
                      data-testid="sig-corps-input"
                      className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border)' }} />
                  </div>
                </>
              )}

              {/* ── Canvas pour tablette et ordinateur ─────────────── */}
              {(sigMode === 'tablette' || sigMode === 'ordinateur') && (
                <div>
                  <label className="text-xs font-semibold block mb-1" style={{ color: 'var(--text-secondary)' }}>Zone de signature</label>
                  <div style={{ border: '2px dashed var(--border)', borderRadius: '10px', overflow: 'hidden', position: 'relative', touchAction: 'none' }}>
                    <canvas ref={sigCanvasRef} width={540} height={180}
                      style={{ display: 'block', width: '100%', cursor: 'crosshair', background: '#FAFAFA' }}
                      onMouseDown={sigStartDraw} onMouseMove={sigDraw} onMouseUp={sigStopDraw} onMouseLeave={sigStopDraw}
                      onTouchStart={sigStartDraw} onTouchMove={sigDraw} onTouchEnd={sigStopDraw}
                    />
                    {!sigHasDrawing && (
                      <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', color: '#CBD5E1', fontSize: '0.8rem', pointerEvents: 'none' }}>
                        Signez ici
                      </span>
                    )}
                  </div>
                  <button onClick={sigClearCanvas} className="mt-1.5 text-xs" style={{ color: 'var(--text-muted)' }}>Effacer</button>
                </div>
              )}

              {/* ── Confirmation signature reçue ────────────────────── */}
              {sigMode === 'recue' && (
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Confirmez que le contrat <strong>{editing?.numero}</strong> a bien été signé par le client (retour papier ou confirmation orale).
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t" style={{ borderColor: 'var(--border)' }}>
              <button onClick={() => setShowSigModal(false)} className="px-4 py-2 rounded-xl text-sm font-medium hover:bg-gray-100"
                style={{ color: 'var(--text-secondary)' }}>
                Annuler
              </button>
              <button
                disabled={savingSig
                  || ((sigMode === 'tablette' || sigMode === 'ordinateur') && (!sigHasDrawing || !signaireNom.trim()))
                  || ((sigMode === 'mail' || sigMode === 'lien' || sigMode === 'relien') && (!signaireNom.trim() || !sigEmail.trim()))}
                onClick={() => {
                  if (sigMode === 'tablette' || sigMode === 'ordinateur') handleSignerTerrain();
                  else if (sigMode === 'mail') handleEnvoyerMail();
                  else if (sigMode === 'lien' || sigMode === 'relien') handleEnvoyerLien();
                  else if (sigMode === 'recue') handleSignatureRecue();
                }}
                data-testid="sig-modal-submit"
                className="px-5 py-2 rounded-xl text-sm font-semibold text-white"
                style={{ background: '#4338CA', opacity: savingSig ? 0.6 : 1, cursor: savingSig ? 'not-allowed' : 'pointer' }}>
                {savingSig ? 'Envoi…' : (
                  sigMode === 'tablette' || sigMode === 'ordinateur' ? 'Valider la signature' :
                  sigMode === 'mail' ? 'Envoyer le PDF' :
                  (sigMode === 'lien' || sigMode === 'relien') ? 'Envoyer le lien' :
                  'Confirmer la signature'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      </SidePanel>

      {/* ─── EmailComposeModal — Envoi devis V2 (hors SidePanel = Dialog.Portal OK) ─── */}
      <EmailComposeModal
        open={emailDevisOpen}
        onClose={() => setEmailDevisOpen(false)}
        onSend={handleSendEmailDevisV2}
        destinataires={(() => {
          const email = editing?.contact_email || editing?.client_email || '';
          const nom   = editing?.contact_nom   || editing?.client_nom   || '';
          return email ? [{ email, nom, source: 'document' }] : [];
        })()}
        entrepriseId={editing?.client_type === 'entreprise' ? editing?.client_id : null}
        agenceId={editing?.agence_id || null}
        typeCode="devis_location_v2"
        templateVariables={{
          nom_client:    editing?.client_nom || '',
          numero_devis:  mailDevisAvenantId
            ? (avenants.find(a => a.avenant_id === mailDevisAvenantId)?.numero || '')
            : (editing?.numero || ''),
          nom_emetteur: '',
        }}
        titre={mailDevisAvenantId ? "Envoyer le devis d'avenant par email" : "Envoyer le devis par email"}
        documentRef={mailDevisAvenantId
          ? (avenants.find(a => a.avenant_id === mailDevisAvenantId)?.numero || '')
          : (editing?.numero || '')}
        infoNote="Le PDF du devis sera automatiquement ajouté en pièce jointe."
        sending={sendingEmailDevis}
        creatable={true}
      />

      {/* ─── EmailComposeModal — Envoi contrat signé ─── */}
      <EmailComposeModal
        open={emailContratSigneOpen}
        onClose={() => setEmailContratSigneOpen(false)}
        onSend={async ({ emails, sujet, corps }) => {
          setSendingEmailContratSigne(true);
          try {
            await api.envoyerContratSigneV2(editing.contrat_id, { destinataires: emails, sujet, corps });
            toast.success(`Contrat signé envoyé à ${emails.join(', ')}`);
            setEmailContratSigneOpen(false);
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur envoi email');
          } finally { setSendingEmailContratSigne(false); }
        }}
        destinataires={(() => {
          const email = editing?.contact_email || editing?.client_email || '';
          const nom   = editing?.contact_nom   || editing?.client_nom   || '';
          return email ? [{ email, nom, source: 'document' }] : [];
        })()}
        entrepriseId={editing?.client_type === 'entreprise' ? editing?.client_id : null}
        agenceId={editing?.agence_id || null}
        titre="Envoyer le contrat signé par email"
        documentRef={editing?.numero || ''}
        infoNote="Le PDF du contrat signé sera automatiquement ajouté en pièce jointe."
        sending={sendingEmailContratSigne}
        creatable={true}
      />

      {/* ─── EmailComposeModal — Envoi facture V2 (parité V1) ─── */}
      <EmailComposeModal
        open={factureEmailPanel.open}
        onClose={() => setFactureEmailPanel(p => ({ ...p, open: false }))}
        onSend={handleConfirmFactureEmail}
        destinataires={factureEmailPanel.destinataires}
        loadingDestinataires={factureEmailPanel.loading}
        typeCode="facture_location"
        templateVariables={{
          nom_client:       editing?.client_nom || '',
          numero_facture:   factureEmailPanel.factureMeta?.ext_invoice_number || activePLFac?.ext_invoice_number || '',
          date_facture:     (factureEmailPanel.factureMeta?.date_facture || activePLFac?.date_facture)
            ? new Date(factureEmailPanel.factureMeta?.date_facture || activePLFac?.date_facture).toLocaleDateString('fr-FR') : '',
          total_ttc:        (factureEmailPanel.factureMeta?.montant_ttc ?? activePLFac?.montant_ttc) != null
            ? `${Number(factureEmailPanel.factureMeta?.montant_ttc ?? activePLFac?.montant_ttc).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} EUR` : '',
          nom_emetteur:     user ? (`${user.prenom || ''} ${user.nom || ''}`.trim() || user.name || user.email || '') : '',
          nom_destinataire: factureEmailPanel.destinataires?.[0]?.nom || '',
        }}
        agenceId={editing?.agence_id || ''}
        entrepriseId={factureEmailPanel.factureMeta?.entreprise_id || (editing?.client_type === 'entreprise' ? editing?.client_id : null)}
        titre="Envoyer la facture par email"
        documentRef={activePLFac?.ext_invoice_number ? `Facture ${activePLFac.ext_invoice_number}` : ''}
        infoNote="Le PDF de la facture sera automatiquement ajouté en pièce jointe."
        sending={envoyantFactureEmail}
        creatable={true}
      />

      {/* ─── Panel brouillon Pennylane (parité V1 — révision humaine avant émission) ─── */}
      {plDraftPanel !== null && createPortal(
        <div
          style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(0,0,0,0.45)', pointerEvents:'auto' }}
          onPointerDown={e => e.stopPropagation()}
          onClick={e => { e.stopPropagation(); if (e.target === e.currentTarget) closePlDraftPanel(); }}
        >
          <div
            style={{ position:'fixed', top:0, right:0, width:'min(1360px,92vw)', height:'100vh', background:'#ffffff', boxShadow:'-4px 0 32px rgba(0,0,0,0.18)', display:'flex', flexDirection:'column', pointerEvents:'auto' }}
            onPointerDown={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 24px', borderBottom:'1px solid #E5E7EB', flexShrink:0 }}>
              <div>
                <h2 style={{ fontSize:15, fontWeight:700, color:'#111827', margin:0 }}>Brouillon Pennylane</h2>
                <p style={{ fontSize:12, color:'#6B7280', margin:'2px 0 0 0' }}>Vérifiez la facture puis validez ou saisissez le règlement</p>
              </div>
              <button data-testid="pl-panel-close" onClick={closePlDraftPanel} style={{ background:'none', border:'none', cursor:'pointer', padding:4, color:'#6B7280' }}>
                <XIcon size={18} />
              </button>
            </div>
            <div style={{ flex:1, overflow:'hidden', background:'#F9FAFB' }}>
              {plDraftPanel.loading ? (
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', gap:12 }}>
                  <Loader2 size={32} className="animate-spin" style={{ color:'#0EA5E9' }} />
                  <span style={{ fontSize:13, color:'#6B7280' }}>Chargement du PDF…</span>
                </div>
              ) : plDraftPanel.pdf_blob_url ? (
                <iframe src={plDraftPanel.pdf_blob_url} title="Brouillon facture Pennylane" style={{ width:'100%', height:'100%', border:'none' }} />
              ) : (
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', gap:12, padding:32 }}>
                  <FileText size={40} style={{ color:'#D1D5DB' }} />
                  <p style={{ fontSize:13, color:'#6B7280', textAlign:'center', margin:0 }}>PDF non disponible pour le moment.</p>
                </div>
              )}
            </div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 24px', borderTop:'1px solid #E5E7EB', gap:10, flexShrink:0 }}>
              {activePLFac?.ext_status !== 'posted' ? (
                <button data-testid="pl-panel-supprimer" onClick={handleSupprimerDraftPL} disabled={supprimantDraftPL || validantPL}
                  style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:10, fontSize:13, fontWeight:600, background:'none', border:'1px solid #FECACA', color:'#DC2626', cursor:'pointer', opacity:(supprimantDraftPL||validantPL)?0.5:1 }}>
                  {supprimantDraftPL ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  Supprimer brouillon
                </button>
              ) : <span />}
              <div style={{ display:'flex', gap:10 }}>
                <button onClick={closePlDraftPanel} style={{ padding:'8px 16px', borderRadius:10, fontSize:13, fontWeight:600, background:'none', border:'1px solid #E5E7EB', color:'#374151', cursor:'pointer' }}>Fermer</button>
                <button data-testid="pl-panel-reglement" onClick={() => setShowReglPLPanel(true)}
                  style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:10, fontSize:13, fontWeight:600, background:reglPLSaisi?'rgba(34,197,94,0.1)':'none', border:reglPLSaisi?'1px solid rgba(34,197,94,0.5)':'1px solid #E5E7EB', color:reglPLSaisi?'#16a34a':'#374151', cursor:'pointer' }}>
                  <CreditCard size={13} />{reglPLSaisi ? (activePLFac?.ext_status==='posted'?'Voir le règlement':'Règlement saisi ✓') : 'Saisir le règlement'}
                </button>
                {activePLFac?.ext_status !== 'posted' && (
                  <button data-testid="pl-panel-valider" onClick={handleValiderPL} disabled={validantPL || supprimantDraftPL}
                    style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 20px', borderRadius:10, fontSize:13, fontWeight:700, background:'#0EA5E9', color:'#ffffff', border:'none', cursor:'pointer', opacity:(validantPL||supprimantDraftPL)?0.5:1 }}>
                    {validantPL ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                    Valider la facture
                  </button>
                )}
                {/* Facture acquittée — visible si finalisée + entièrement payée (parité V1) */}
                {activePLFac?.ext_status === 'posted' &&
                 (activePLFac?.statut_paiement === 'payee' ||
                  (reglPLSaisi?.restant !== undefined && reglPLSaisi.restant <= 0 && reglPLSaisi.reglements?.length > 0)) && (
                  <button
                    data-testid="pl-panel-acquittee"
                    onClick={handleAcquittee}
                    disabled={downloadingAcquittee}
                    style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:10, fontSize:13, fontWeight:600, background:'#F0FDF4', border:'1px solid #BBF7D0', color:'#15803D', cursor:'pointer', opacity: downloadingAcquittee ? 0.6 : 1 }}>
                    {downloadingAcquittee ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                    Facture acquittée
                  </button>
                )}
                {/* Envoyer la facture par email — visible si finalisée (parité V1) */}
                {activePLFac?.ext_status === 'posted' && (
                  <button
                    data-testid="pl-panel-envoyer-email"
                    onClick={handleEnvoyerFactureEmail}
                    disabled={envoyantFactureEmail}
                    style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:10, fontSize:13, fontWeight:600, background:'none', border:'1px solid #0EA5E9', color:'#0EA5E9', cursor:'pointer', opacity: envoyantFactureEmail ? 0.5 : 1 }}>
                    {envoyantFactureEmail ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
                    Envoyer la facture par email
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Panel règlement PL (au-dessus du panel brouillon) */}
      {showReglPLPanel && activePLFac?.facture_id && createPortal(
        <Dialog.Root open={true}>
          <Dialog.Content
            data-ignore-outside
            asChild
            onInteractOutside={e => e.preventDefault()}
            onOpenAutoFocus={e => e.preventDefault()}
            onCloseAutoFocus={e => e.preventDefault()}
          >
            <div style={{ position:'fixed', inset:0, zIndex:10500, pointerEvents:'none' }}>
              <ReglementPanel
                devis={{ vente_id: activePLFac.facture_id }}
                onClose={() => setShowReglPLPanel(false)}
                onSaved={(d) => { setReglPLSaisi(d?.reglements?.length>0?d:null); setShowReglPLPanel(false); }}
                apiFns={{
                  get:    () => api.getReglementsFactureLoc(activePLFac.facture_id),
                  create: (d) => api.createReglementFactureLoc(activePLFac.facture_id, d),
                  delete: (rid) => api.deleteReglementFactureLoc(activePLFac.facture_id, rid),
                }}
              />
            </div>
          </Dialog.Content>
        </Dialog.Root>,
        document.body
      )}

      {/* ─── Modale "Préparer pour livraison terrain" ─────────────────────────────── */}

      {/* Modales déplacées DANS le SidePanel (pattern V1) */}

      {/* Modal signature déplacée DANS le SidePanel (pattern V1) */}

      {/* ── Drawer consultation contrôles BL V2 ── */}
      {voirControlesBlId && (
        <div className="fixed inset-0 flex" style={{ background: 'rgba(0,0,0,0.4)', zIndex: 10000, pointerEvents: 'auto' }}
          onClick={() => { setVoirControlesBlId(null); setVoirBlDetail(null); }}>
          <div className="ml-auto h-full overflow-y-auto"
            style={{ width: 480, background: 'var(--card)', boxShadow: '-4px 0 32px rgba(0,0,0,0.12)' }}
            onClick={e => e.stopPropagation()}>

            {/* Header drawer */}
            <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 z-10"
              style={{ borderColor: 'var(--border)', background: 'var(--card)' }}>
              <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>Fiche d'inspection</p>
              <button onClick={() => { setVoirControlesBlId(null); setVoirBlDetail(null); }} className="p-1.5 rounded-lg hover:bg-gray-100">
                <XIcon size={16} style={{ color: 'var(--text-secondary)' }} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Infos BL : signataire / agent / date réelle (même contenu que la lecture terrain) */}
              {voirBlDetail && (voirBlDetail.signataire_nom || voirBlDetail.rempli_par || voirBlDetail.date_effective) && (
                <div className="rounded-xl border p-3 space-y-1.5" style={{ borderColor: 'var(--border)' }} data-testid="bl-drawer-infos">
                  {voirBlDetail.signataire_nom && (
                    <div className="flex justify-between text-sm">
                      <span style={{ color: 'var(--text-muted)' }}>Signataire</span>
                      <span className="font-medium text-right" style={{ color: 'var(--text-primary)' }}>{voirBlDetail.signataire_nom}</span>
                    </div>
                  )}
                  {voirBlDetail.rempli_par && (
                    <div className="flex justify-between text-sm">
                      <span style={{ color: 'var(--text-muted)' }}>Agent de contrôle</span>
                      <span className="font-medium text-right" style={{ color: 'var(--text-primary)' }}>{voirBlDetail.rempli_par}</span>
                    </div>
                  )}
                  {voirBlDetail.date_effective && (
                    <div className="flex justify-between text-sm">
                      <span style={{ color: 'var(--text-muted)' }}>Date réelle {voirBlDetail.type_mouvement === 'RETOUR' ? 'du retour' : 'de livraison'}</span>
                      <span className="font-medium text-right" style={{ color: 'var(--text-primary)' }}>{fmtDateTime(voirBlDetail.date_effective, activeSocieteTimezone)}</span>
                    </div>
                  )}
                </div>
              )}
              {loadingControles ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={24} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
                </div>
              ) : controlesBl.length === 0 ? (
                <p className="text-sm text-center py-8" style={{ color: 'var(--text-muted)' }}>
                  Aucune inspection enregistrée pour ce BL.
                </p>
              ) : controlesBl.map((ctl, ci) => (
                <div key={ci} className="space-y-3">
                  {/* En-tête contrôle */}
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
                      {ctl.materiel_nom || `Matériel ${ci + 1}`}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      {ctl.date_inspection ? fmtDateTime(ctl.date_inspection, activeSocieteTimezone) : '—'}
                    </p>
                  </div>

                  {/* Critères */}
                  {(ctl.lignes || []).length > 0 && (
                    <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
                      {ctl.lignes.map((lc, li) => (
                        <div key={li} className="flex items-center gap-3 px-3 py-2.5 border-b last:border-0 text-sm"
                          style={{ borderColor: 'var(--border-light)' }}>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate" style={{ color: 'var(--text-secondary)' }}>{lc.nom_critere}</p>
                            <p style={{ color: lc.valeur_mesuree ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                              {lc.valeur_mesuree || '—'}{lc.unite ? ` ${lc.unite}` : ''}
                            </p>
                          </div>
                          {lc.photo_url && (
                            <a href={lc.photo_url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                              <img src={lc.photo_url} alt="" className="w-10 h-10 rounded-lg object-cover border"
                                style={{ borderColor: 'var(--border)' }} />
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Photos générales */}
                  {(ctl.photos_urls || []).length > 0 && (
                    <div>
                      <p className="text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>Photos générales</p>
                      <div className="grid grid-cols-3 gap-2">
                        {ctl.photos_urls.map((url, pi) => (
                          <a key={pi} href={url} target="_blank" rel="noopener noreferrer"
                            className="rounded-lg overflow-hidden" style={{ aspectRatio: '1', display: 'block' }}>
                            <img src={url} alt="" className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {/* PDF BL signé (parité avec la lecture terrain) */}
              {voirBlDetail?.pdf_bl_signe_url && (
                <a href={voirBlDetail.pdf_bl_signe_url} target="_blank" rel="noopener noreferrer"
                  data-testid="bl-drawer-pdf-signe"
                  className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border text-sm font-semibold"
                  style={{ borderColor: 'var(--border)', color: 'var(--text-primary)', background: 'var(--bg)' }}>
                  <Eye size={14} /> Voir le PDF signé
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
